import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import {
  FaCogs,
  FaCode,
  FaPaintBrush,
  FaChartLine,
  FaCalculator,
  FaHandshake,
} from "react-icons/fa";

const TeamData = {
  "Product": {
    icon: FaCogs,
    color: "#4A90E2",
    description:
      "Our product managers work closely with founders to set initial goals and ensure alignment with startup objectives.",
    tasks: [
      "Set and track initial goals",
      "Coordinate with team members",
      "Manage product development timelines",
      "Conduct user research",
    ],
  },
  Engineering: {
    icon: FaCode,
    color: "#50E3C2",
    description:
      "Our engineering team works closely with startups to develop and implement technical solutions throughout the project.",
    tasks: [
      "Develop and implement technical solutions",
      "Pair with startups based on required skill sets",
      "Provide technical support",
      "Innovate and improve technical processes",
    ],
  },
  Design: {
    icon: FaPaintBrush,
    color: "#F5A623",
    description:
      "Our design team will create user-friendly and aesthetically pleasing designs that enhance brand identity and user experience.",
    tasks: [
      "Create user-friendly designs",
      "Develop brand identity and visual assets",
      "Collaborate on UI/UX",
      "Enhance overall user experience",
    ],
  },
  Marketing: {
    icon: FaChartLine,
    color: "#D0021B",
    description:
      "Our marketing team will develop and execute marketing strategies to increase visibility and identify market opportunities.",
    tasks: [
      "Develop marketing strategies",
      "Execute marketing campaigns",
      "Analyze market trends",
      "Identify and capitalize on opportunities",
    ],
  },
  Finance: {
    icon: FaCalculator,
    color: "#7ED321",
    description:
      "The finance team will assist with financial planning and budgeting, ensuring startups are financially sound and investor-ready.",
    tasks: [
      "Assist with financial planning and budgeting",
      "Advise on funding strategies",
      "Ensure financial stability",
      "Prepare startups for investor readiness",
    ],
  },
  "Venture Capital": {
    icon: FaHandshake,
    color: "#9013FE",
    description:
      "The Venture Capital team will provide insights into the venture capital landscape and prepare startups for fundraising and investor pitches.",
    tasks: [
      "Offer insights into the VC landscape",
      "Prepare for fundraising",
      "Advise on investor pitches",
      "Connect with potential investors and partners",
    ],
  },
};

const ProductManagementAnimation = () => {
  const circleRadius = 62.5;
  const centerTop = { x: 142, y: 24 };
  const centerBottom = { x: 24, y: 142 };

  const nodeVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut"
      }
    }
  };

  return (
    <svg width="166" height="166" viewBox="0 0 166 166" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_1780_167)">
        <rect width="166" height="166" rx="13.5" fill="white"/>
        <g opacity="0.9" filter="url(#filter0_d_1780_167)">
          <rect width="166" height="166" rx="13.4959" fill="white"/>
        </g>
        
        <motion.circle
          cx={centerTop.x}
          cy={centerTop.y}
          r="59"
          fill="url(#paint0_linear_1780_167)"
          fillOpacity="0.5"
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.7, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.circle
          cx={centerTop.x}
          cy={centerTop.y}
          r={circleRadius}
          stroke="#F2994A"
          strokeWidth="1"
          strokeLinecap="round"
          animate={{
            rotate: 360,
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <motion.circle
          cx={centerBottom.x}
          cy={centerBottom.y}
          r="59"
          fill="url(#paint1_linear_1780_167)"
          fillOpacity="0.5"
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.7, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        <motion.circle
          cx={centerBottom.x}
          cy={centerBottom.y}
          r={circleRadius}
          stroke="#F2994A"
          strokeWidth="1"
          strokeLinecap="round"
          animate={{
            rotate: -360,
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        {[0, 1, 2, 3].map((i) => (
          <motion.circle
            key={`node-top-${i}`}
            cx={centerTop.x + circleRadius * Math.cos(i * Math.PI / 2)}
            cy={centerTop.y + circleRadius * Math.sin(i * Math.PI / 2)}
            r="3"
            fill="#F19E18"
            variants={nodeVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: i * 0.5 }}
          />
        ))}

        {[0, 1, 2, 3].map((i) => (
          <motion.circle
            key={`node-bottom-${i}`}
            cx={centerBottom.x + circleRadius * Math.cos(i * Math.PI / 2)}
            cy={centerBottom.y + circleRadius * Math.sin(i * Math.PI / 2)}
            r="3"
            fill="#E62314"
            variants={nodeVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: i * 0.5 + 2 }}
          />
        ))}

        {/* Flowing particles between circles */}
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.circle
            key={`particle-${i}`}
            r="6"
            fill="#F2994A"
            className={"outline rounded-full outline-offset-2 outline-[#F2994A]"}
            initial={{ x: 0, y: 0 }}
            animate={{
              x: [0, 166],
              y: [0, 166],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: i * 0.8,
              ease: "linear"
            }}
          />
        ))}

      </g>
      <defs>
        <filter id="filter0_d_1780_167" x="-77.2455" y="-72.0958" width="325.641" height="325.641" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feMorphology radius="2.57485" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_1780_167"/>
          <feOffset dx="2.57485" dy="7.72455"/>
          <feGaussianBlur stdDeviation="38.6228"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1780_167"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1780_167" result="shape"/>
        </filter>
        <linearGradient id="paint0_linear_1780_167" x1="65.551" y1="73.5746" x2="218.449" y2="-79.3233" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E62314"/>
          <stop offset="1" stopColor="#E62314" stopOpacity="0"/>
        </linearGradient>
        <linearGradient id="paint1_linear_1780_167" x1="24" y1="7.01048" x2="24" y2="223.241" gradientUnits="userSpaceOnUse">
          <stop stopColor="#F19E18"/>
          <stop offset="1" stopColor="#F19E18" stopOpacity="0"/>
        </linearGradient>
        <clipPath id="clip0_1780_167">
          <rect width="166" height="166" rx="13.5" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
};

const EngineeringAnimation = () => (
  <svg
    width="226"
    height="268"
    viewBox="0 0 226 268"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <motion.rect
      x="23.25"
      y="38.25"
      width="178.5"
      height="114.271"
      rx="10.793"
      fill="white"
      stroke="#F2F2F2"
      stroke-width="0.5"
      animate={{ scale: [1.05, 1, 1.05], opacity: [1, 0.6, 1] }}
      transition={{
        duration: 4,
        ease: "easeInOut",
        loop: Infinity,
        times: [0, 0.5, 1],
        repeat: Infinity,
        delay: 1.5,
      }}
    />
    <motion.g filter="url(#filter0_d_680_1635)">
      <g opacity="0.8">
        <path
          d="M45.3361 58.5322C45.199 58.5322 45.0722 58.4979 44.9557 58.4293C44.8391 58.3608 44.7466 58.2682 44.678 58.1517C44.6095 58.0352 44.5752 57.9084 44.5752 57.7713C44.5752 57.6342 44.6095 57.5073 44.678 57.3908C44.7466 57.2743 44.8391 57.1817 44.9557 57.1132C45.0722 57.0446 45.199 57.0103 45.3361 57.0103C45.4732 57.0103 45.6 57.0446 45.7166 57.1132C45.8331 57.1817 45.9256 57.2743 45.9942 57.3908C46.0627 57.5073 46.097 57.6342 46.097 57.7713C46.097 57.9084 46.0627 58.0352 45.9942 58.1517C45.9256 58.2682 45.8331 58.3608 45.7166 58.4293C45.6 58.4979 45.4732 58.5322 45.3361 58.5322ZM45.9428 63.9511H47.6189V64.8457H42.7757V63.9511H44.8528V60.3008H43.0842V59.3959H45.9428V63.9511ZM52.7396 58.0078C51.7662 58.0078 51.2795 58.6076 51.2795 59.8072V60.6298H53.5108V61.5347H51.2795V64.8457H50.1895V61.5347H48.4723V60.6298H50.1895V59.8586C50.1895 58.9126 50.4037 58.2048 50.8322 57.7353C51.2606 57.2657 51.9067 57.0309 52.7704 57.0309C53.1612 57.0309 53.5382 57.0652 53.9015 57.1337L53.7884 58.0797C53.384 58.0317 53.0344 58.0078 52.7396 58.0078ZM59.1765 65.9768C58.4362 65.9768 57.8021 65.7883 57.2742 65.4112C56.7498 65.0342 56.3488 64.4858 56.0712 63.766C55.797 63.0462 55.6599 62.1791 55.6599 61.1645C55.6599 60.1637 55.797 59.3085 56.0712 58.599C56.3488 57.8861 56.7498 57.3445 57.2742 56.9744C57.8021 56.6007 58.4362 56.4139 59.1765 56.4139H59.4233L59.3205 57.288H59.2794C58.7309 57.288 58.2631 57.4405 57.8758 57.7455C57.4885 58.0472 57.1937 58.4893 56.9915 59.072C56.7892 59.6513 56.6881 60.3488 56.6881 61.1645C56.6881 61.994 56.7892 62.7035 56.9915 63.293C57.1937 63.8826 57.4885 64.3316 57.8758 64.6401C58.2631 64.9485 58.7309 65.1028 59.2794 65.1028H59.3205L59.4233 65.9768H59.1765ZM117.428 59.437H126.888V60.3933H117.428V59.437ZM117.428 62.0077H126.888V62.964H117.428V62.0077ZM139.947 64.8251C139.463 64.9074 138.987 64.9485 138.517 64.9485C137.962 64.9485 137.513 64.8731 137.17 64.7223C136.827 64.5715 136.576 64.3384 136.414 64.0231C136.253 63.7078 136.173 63.2965 136.173 62.7892V60.3008H134.805V59.3959H136.173V57.7507H137.263V59.3959H139.741V60.3008H137.263V62.7378C137.263 63.1731 137.369 63.4884 137.582 63.6838C137.794 63.8757 138.14 63.9717 138.62 63.9717C138.778 63.9717 138.946 63.9631 139.124 63.946C139.306 63.9288 139.539 63.9031 139.823 63.8689L139.947 64.8251ZM144.985 59.2931C145.362 59.2931 145.667 59.3651 145.9 59.509C146.137 59.6495 146.31 59.8689 146.419 60.1671C146.533 60.4653 146.589 60.856 146.589 61.3393H145.509C145.509 60.9452 145.449 60.6607 145.33 60.4859C145.213 60.3076 145.023 60.2185 144.759 60.2185C144.303 60.2185 143.958 60.4139 143.725 60.8046C143.496 61.1954 143.381 61.7609 143.381 62.5013V63.9408H144.718V64.8457H141.078V63.9408H142.291V60.3008H141.283V59.3959H143.175L143.36 60.7943H143.484C143.515 60.2905 143.653 59.9152 143.9 59.6684C144.15 59.4182 144.512 59.2931 144.985 59.2931ZM152.79 63.9717V64.8457L151.042 64.8971L150.964 63.7969H150.867C150.784 64.167 150.627 64.4515 150.394 64.6503C150.164 64.8491 149.88 64.9485 149.54 64.9485C148.934 64.9485 148.464 64.7789 148.132 64.4395C147.802 64.1002 147.638 63.6186 147.638 62.9948V59.2931H148.707V62.9948C148.707 63.3239 148.798 63.5775 148.98 63.7557C149.162 63.934 149.417 64.0231 149.746 64.0231C150.15 64.0231 150.452 63.8911 150.651 63.6272C150.85 63.3599 150.949 62.9263 150.949 62.3265V59.2931H152.039V63.8586L152.79 63.9717ZM158.476 61.9049C158.476 62.1551 158.466 62.3882 158.445 62.6041H154.764C154.86 63.0565 155.076 63.401 155.412 63.6375C155.748 63.874 156.19 63.9922 156.738 63.9922C157.16 63.9922 157.605 63.9443 158.075 63.8483L158.209 64.8046C157.705 64.9005 157.173 64.9485 156.615 64.9485C155.994 64.9485 155.466 64.8388 155.031 64.6195C154.599 64.4001 154.27 64.0796 154.044 63.6581C153.818 63.233 153.705 62.7172 153.705 62.1105C153.705 61.521 153.801 61.0154 153.993 60.5938C154.185 60.1722 154.464 59.8501 154.831 59.6273C155.201 59.4045 155.645 59.2931 156.162 59.2931C156.903 59.2931 157.473 59.5176 157.874 59.9666C158.275 60.4156 158.476 61.0617 158.476 61.9049ZM157.489 61.7712C157.489 61.2845 157.372 60.9092 157.139 60.6452C156.909 60.3813 156.587 60.2494 156.173 60.2494C155.751 60.2494 155.415 60.3813 155.165 60.6452C154.915 60.9092 154.767 61.2845 154.723 61.7712H157.489ZM160.358 65.1028C160.906 65.1028 161.374 64.9485 161.761 64.6401C162.148 64.3316 162.443 63.8826 162.645 63.293C162.848 62.7035 162.949 61.994 162.949 61.1645C162.949 60.3488 162.848 59.6513 162.645 59.072C162.443 58.4893 162.148 58.0472 161.761 57.7455C161.374 57.4405 160.906 57.288 160.358 57.288H160.316L160.214 56.4139H160.46C161.201 56.4139 161.833 56.6007 162.358 56.9744C162.885 57.3445 163.286 57.8861 163.561 58.599C163.838 59.3085 163.977 60.1637 163.977 61.1645C163.977 62.1791 163.838 63.0462 163.561 63.766C163.286 64.4858 162.885 65.0342 162.358 65.4112C161.833 65.7883 161.201 65.9768 160.46 65.9768H160.214L160.316 65.1028H160.358ZM169.746 65.9974C169.345 65.9974 169 65.9082 168.712 65.73C168.428 65.5518 168.21 65.293 168.059 64.9537C167.908 64.6143 167.833 64.2082 167.833 63.7352C167.833 63.4267 167.835 63.1953 167.838 63.0411L167.843 62.3162C167.843 62.1379 167.778 61.994 167.648 61.8843C167.518 61.7746 167.362 61.6958 167.18 61.6478C166.998 61.5998 166.825 61.5758 166.661 61.5758V60.7635C166.808 60.7635 166.974 60.7327 167.16 60.671C167.348 60.6093 167.509 60.5236 167.643 60.4139C167.776 60.3008 167.843 60.1705 167.843 60.0232L167.838 59.2828C167.835 59.132 167.833 58.9058 167.833 58.6041C167.833 57.9118 167.999 57.3771 168.332 57.0001C168.668 56.623 169.139 56.4345 169.746 56.4345C170.02 56.4345 170.26 56.4688 170.465 56.5373L170.342 57.4114C170.136 57.3428 169.968 57.3085 169.838 57.3085C169.547 57.3085 169.322 57.4216 169.165 57.6479C169.01 57.8707 168.933 58.1894 168.933 58.6041C168.933 58.7207 168.935 58.8544 168.938 59.0052C168.942 59.156 168.945 59.2725 168.949 59.3548C168.959 59.5947 168.964 59.8449 168.964 60.1054C168.964 60.4105 168.882 60.6487 168.717 60.8201C168.556 60.988 168.327 61.0754 168.028 61.0823V61.2571C168.323 61.2639 168.553 61.3548 168.717 61.5296C168.882 61.7009 168.964 61.9357 168.964 62.2339C168.964 62.3607 168.962 62.503 168.959 62.6606C168.956 62.8183 168.952 62.9366 168.949 63.0154C168.938 63.2519 168.933 63.4918 168.933 63.7352C168.933 64.1808 169.01 64.5235 169.165 64.7634C169.322 65.0034 169.547 65.1233 169.838 65.1233C169.968 65.1233 170.136 65.0891 170.342 65.0205L170.465 65.8945C170.26 65.9631 170.02 65.9974 169.746 65.9974Z"
          fill="#737373"
        />
        <path
          d="M63.8448 58.5322C63.7077 58.5322 63.5809 58.4979 63.4644 58.4293C63.3478 58.3608 63.2553 58.2682 63.1867 58.1517C63.1182 58.0352 63.0839 57.9084 63.0839 57.7713C63.0839 57.6342 63.1182 57.5073 63.1867 57.3908C63.2553 57.2743 63.3478 57.1817 63.4644 57.1132C63.5809 57.0446 63.7077 57.0103 63.8448 57.0103C63.9819 57.0103 64.1088 57.0446 64.2253 57.1132C64.3418 57.1817 64.4344 57.2743 64.5029 57.3908C64.5715 57.5073 64.6058 57.6342 64.6058 57.7713C64.6058 57.9084 64.5715 58.0352 64.5029 58.1517C64.4344 58.2682 64.3418 58.3608 64.2253 58.4293C64.1088 58.4979 63.9819 58.5322 63.8448 58.5322ZM64.4515 63.9511H66.1276V64.8457H61.2845V63.9511H63.3616V60.3008H61.5929V59.3959H64.4515V63.9511ZM69.2946 64.9485C68.8765 64.9485 68.52 64.9365 68.2252 64.9125C67.9339 64.8885 67.67 64.8491 67.4335 64.7943L67.5569 63.8072C68.2561 63.9168 68.8353 63.9717 69.2946 63.9717C69.843 63.9717 70.2423 63.9186 70.4926 63.8123C70.7428 63.7026 70.8679 63.5295 70.8679 63.293C70.8679 63.1354 70.825 63.0154 70.7393 62.9331C70.6571 62.8509 70.5217 62.7858 70.3332 62.7378L69.0787 62.419C68.7291 62.3299 68.4497 62.2271 68.2407 62.1105C68.0316 61.994 67.8756 61.8466 67.7728 61.6684C67.67 61.4867 67.6186 61.2605 67.6186 60.9897C67.6186 60.5887 67.7145 60.2648 67.9065 60.018C68.1018 59.7678 68.4035 59.5844 68.8113 59.4679C69.2227 59.3514 69.7573 59.2931 70.4154 59.2931C70.9159 59.2931 71.3957 59.3274 71.855 59.3959L71.7419 60.3419C71.2243 60.2939 70.7719 60.2699 70.3846 60.2699C69.8088 60.2699 69.3923 60.3231 69.1352 60.4293C68.8782 60.5321 68.7497 60.7018 68.7497 60.9383C68.7497 61.0891 68.7942 61.2022 68.8833 61.2776C68.9724 61.353 69.1233 61.4182 69.3358 61.473L70.3435 61.7198C70.7342 61.8158 71.0495 61.9289 71.2895 62.0591C71.5294 62.1894 71.7076 62.3522 71.8242 62.5475C71.9407 62.7429 71.999 62.9845 71.999 63.2725C71.999 63.6666 71.9047 63.9871 71.7162 64.2339C71.5277 64.4772 71.2346 64.6572 70.837 64.7737C70.4429 64.8903 69.9287 64.9485 69.2946 64.9485ZM75.8858 64.9485C74.0898 64.9485 73.1917 63.7146 73.1917 61.2468C73.1917 58.7104 74.0898 57.4422 75.8858 57.4422C77.6818 57.4422 78.5798 58.7104 78.5798 61.2468C78.5798 63.7146 77.6818 64.9485 75.8858 64.9485ZM75.8858 64.0025C76.962 64.0025 77.5002 63.0839 77.5002 61.2468C77.5002 59.3411 76.962 58.3882 75.8858 58.3882C74.8095 58.3882 74.2714 59.3411 74.2714 61.2468C74.2714 63.0839 74.8095 64.0025 75.8858 64.0025ZM79.3819 59.3959H80.5438L82.0348 64.0231H82.1273L83.5669 59.3959H84.7289L82.806 64.9485H81.3562L79.3819 59.3959ZM90.6105 61.9049C90.6105 62.1551 90.6002 62.3882 90.5797 62.6041H86.8985C86.9945 63.0565 87.2104 63.401 87.5463 63.6375C87.8822 63.874 88.3243 63.9922 88.8728 63.9922C89.2943 63.9922 89.7399 63.9443 90.2095 63.8483L90.3432 64.8046C89.8393 64.9005 89.3081 64.9485 88.7494 64.9485C88.129 64.9485 87.6011 64.8388 87.1658 64.6195C86.734 64.4001 86.4049 64.0796 86.1787 63.6581C85.9525 63.233 85.8394 62.7172 85.8394 62.1105C85.8394 61.521 85.9353 61.0154 86.1273 60.5938C86.3192 60.1722 86.5986 59.8501 86.9653 59.6273C87.3355 59.4045 87.7794 59.2931 88.2969 59.2931C89.0373 59.2931 89.608 59.5176 90.009 59.9666C90.41 60.4156 90.6105 61.0617 90.6105 61.9049ZM89.6234 61.7712C89.6234 61.2845 89.5068 60.9092 89.2738 60.6452C89.0441 60.3813 88.7219 60.2494 88.3072 60.2494C87.8856 60.2494 87.5497 60.3813 87.2995 60.6452C87.0493 60.9092 86.9019 61.2845 86.8574 61.7712H89.6234ZM95.6284 59.2931C96.0055 59.2931 96.3105 59.3651 96.5436 59.509C96.7801 59.6495 96.9532 59.8689 97.0629 60.1671C97.176 60.4653 97.2325 60.856 97.2325 61.3393H96.1529C96.1529 60.9452 96.0929 60.6607 95.9729 60.4859C95.8564 60.3076 95.6661 60.2185 95.4022 60.2185C94.9464 60.2185 94.6019 60.4139 94.3688 60.8046C94.1392 61.1954 94.0243 61.7609 94.0243 62.5013V63.9408H95.3611V64.8457H91.721V63.9408H92.9344V60.3008H91.9267V59.3959H93.8187L94.0038 60.7943H94.1272C94.158 60.2905 94.2968 59.9152 94.5436 59.6684C94.7938 59.4182 95.1554 59.2931 95.6284 59.2931ZM102.364 65.9768C101.623 65.9768 100.989 65.7883 100.461 65.4112C99.9369 65.0342 99.5358 64.4858 99.2582 63.766C98.984 63.0462 98.8469 62.1791 98.8469 61.1645C98.8469 60.1637 98.984 59.3085 99.2582 58.599C99.5358 57.8861 99.9369 57.3445 100.461 56.9744C100.989 56.6007 101.623 56.4139 102.364 56.4139H102.61L102.508 57.288H102.466C101.918 57.288 101.45 57.4405 101.063 57.7455C100.675 58.0472 100.381 58.4893 100.178 59.072C99.9763 59.6513 99.8752 60.3488 99.8752 61.1645C99.8752 61.994 99.9763 62.7035 100.178 63.293C100.381 63.8826 100.675 64.3316 101.063 64.6401C101.45 64.9485 101.918 65.1028 102.466 65.1028H102.508L102.61 65.9768H102.364ZM104.831 65.1028C105.38 65.1028 105.848 64.9485 106.235 64.6401C106.622 64.3316 106.917 63.8826 107.119 63.293C107.321 62.7035 107.423 61.994 107.423 61.1645C107.423 60.3488 107.321 59.6513 107.119 59.072C106.917 58.4893 106.622 58.0472 106.235 57.7455C105.848 57.4405 105.38 57.288 104.831 57.288H104.79L104.687 56.4139H104.934C105.675 56.4139 106.307 56.6007 106.831 56.9744C107.359 57.3445 107.76 57.8861 108.034 58.599C108.312 59.3085 108.451 60.1637 108.451 61.1645C108.451 62.1791 108.312 63.0462 108.034 63.766C107.76 64.4858 107.359 65.0342 106.831 65.4112C106.307 65.7883 105.675 65.9768 104.934 65.9768H104.687L104.79 65.1028H104.831Z"
          fill="#AC9E22"
        />
      </g>
      <g opacity="0.6">
        <path
          d="M55.9883 81.7962C55.3919 81.7962 54.8846 81.6865 54.4665 81.4671C54.0483 81.2478 53.7296 80.9273 53.5102 80.5057C53.2908 80.0807 53.1812 79.5649 53.1812 78.9582C53.1812 78.3652 53.2823 77.8579 53.4845 77.4364C53.6867 77.0148 53.9832 76.6943 54.3739 76.4749C54.7647 76.2521 55.2377 76.1407 55.7929 76.1407C56.0877 76.1407 56.3859 76.175 56.6875 76.2436C56.9926 76.3121 57.2788 76.4903 57.5461 76.7783C57.8169 77.0627 57.9523 77.498 57.9523 78.0842H56.8315C56.8315 77.7791 56.7407 77.5426 56.559 77.3747C56.3773 77.2033 56.1254 77.1176 55.8032 77.1176C55.3302 77.1176 54.9635 77.2735 54.703 77.5854C54.4425 77.8974 54.3122 78.3378 54.3122 78.9068C54.3122 79.5272 54.4665 80.0019 54.775 80.3309C55.0869 80.6565 55.5324 80.8193 56.1117 80.8193C56.6258 80.8193 57.1605 80.7748 57.7158 80.6857L57.8392 81.6419C57.3422 81.7448 56.7252 81.7962 55.9883 81.7962ZM61.7363 81.7962C61.2359 81.7962 60.8057 81.6865 60.4458 81.4671C60.0894 81.2444 59.8169 80.9222 59.6284 80.5006C59.4433 80.0756 59.3507 79.5649 59.3507 78.9685C59.3507 78.3721 59.4433 77.8631 59.6284 77.4415C59.8169 77.0165 60.0894 76.6943 60.4458 76.4749C60.8057 76.2521 61.2359 76.1407 61.7363 76.1407C62.2367 76.1407 62.6652 76.2521 63.0216 76.4749C63.3815 76.6943 63.654 77.0165 63.8391 77.4415C64.0276 77.8631 64.1219 78.3721 64.1219 78.9685C64.1219 79.5649 64.0276 80.0756 63.8391 80.5006C63.654 80.9222 63.3815 81.2444 63.0216 81.4671C62.6652 81.6865 62.2367 81.7962 61.7363 81.7962ZM61.7363 80.8193C62.1373 80.8193 62.4458 80.66 62.6617 80.3412C62.8811 80.019 62.9908 79.5614 62.9908 78.9685C62.9908 78.3755 62.8811 77.9196 62.6617 77.6009C62.4458 77.2787 62.1373 77.1176 61.7363 77.1176C61.3353 77.1176 61.0251 77.2787 60.8057 77.6009C60.5898 77.9196 60.4818 78.3755 60.4818 78.9685C60.4818 79.5614 60.5898 80.019 60.8057 80.3412C61.0251 80.66 61.3353 80.8193 61.7363 80.8193ZM68.3172 76.1407C68.893 76.1407 69.3386 76.3155 69.6539 76.6652C69.9693 77.0148 70.1269 77.5083 70.1269 78.1459V81.6934H69.037V78.1459C69.037 77.8031 68.9513 77.5375 68.7799 77.3489C68.6085 77.1604 68.3686 77.0662 68.0601 77.0662C67.2032 77.0662 66.7748 77.5872 66.7748 78.6291V81.6934H65.6848V76.2436H66.672L66.7594 77.2307H66.8776C66.9085 76.864 67.0421 76.5915 67.2786 76.4132C67.5151 76.2316 67.8613 76.1407 68.3172 76.1407ZM73.6539 81.7962C73.2357 81.7962 72.8792 81.7842 72.5845 81.7602C72.2931 81.7362 72.0292 81.6968 71.7927 81.6419L71.9161 80.6548C72.6153 80.7645 73.1946 80.8193 73.6539 80.8193C74.2023 80.8193 74.6016 80.7662 74.8518 80.66C75.102 80.5503 75.2271 80.3772 75.2271 80.1407C75.2271 79.983 75.1843 79.8631 75.0986 79.7808C75.0163 79.6985 74.8809 79.6334 74.6924 79.5854L73.4379 79.2667C73.0883 79.1775 72.809 79.0747 72.5999 78.9582C72.3908 78.8416 72.2349 78.6943 72.132 78.516C72.0292 78.3344 71.9778 78.1081 71.9778 77.8374C71.9778 77.4364 72.0738 77.1124 72.2657 76.8657C72.4611 76.6155 72.7627 76.4321 73.1706 76.3155C73.5819 76.199 74.1166 76.1407 74.7747 76.1407C75.2751 76.1407 75.7549 76.175 76.2142 76.2436L76.1011 77.1896C75.5836 77.1416 75.1311 77.1176 74.7438 77.1176C74.168 77.1176 73.7515 77.1707 73.4945 77.277C73.2374 77.3798 73.1089 77.5495 73.1089 77.786C73.1089 77.9368 73.1534 78.0499 73.2426 78.1253C73.3317 78.2007 73.4825 78.2658 73.695 78.3207L74.7027 78.5674C75.0934 78.6634 75.4088 78.7765 75.6487 78.9068C75.8886 79.037 76.0669 79.1998 76.1834 79.3952C76.2999 79.5906 76.3582 79.8322 76.3582 80.1201C76.3582 80.5143 76.2639 80.8348 76.0754 81.0815C75.8869 81.3249 75.5939 81.5048 75.1963 81.6214C74.8021 81.7379 74.288 81.7962 73.6539 81.7962ZM80.245 81.7962C79.7446 81.7962 79.3144 81.6865 78.9546 81.4671C78.5981 81.2444 78.3256 80.9222 78.1371 80.5006C77.952 80.0756 77.8595 79.5649 77.8595 78.9685C77.8595 78.3721 77.952 77.8631 78.1371 77.4415C78.3256 77.0165 78.5981 76.6943 78.9546 76.4749C79.3144 76.2521 79.7446 76.1407 80.245 76.1407C80.7454 76.1407 81.1739 76.2521 81.5304 76.4749C81.8902 76.6943 82.1627 77.0165 82.3478 77.4415C82.5363 77.8631 82.6306 78.3721 82.6306 78.9685C82.6306 79.5649 82.5363 80.0756 82.3478 80.5006C82.1627 80.9222 81.8902 81.2444 81.5304 81.4671C81.1739 81.6865 80.7454 81.7962 80.245 81.7962ZM80.245 80.8193C80.646 80.8193 80.9545 80.66 81.1705 80.3412C81.3898 80.019 81.4995 79.5614 81.4995 78.9685C81.4995 78.3755 81.3898 77.9196 81.1705 77.6009C80.9545 77.2787 80.646 77.1176 80.245 77.1176C79.844 77.1176 79.5338 77.2787 79.3144 77.6009C79.0985 77.9196 78.9905 78.3755 78.9905 78.9685C78.9905 79.5614 79.0985 80.019 79.3144 80.3412C79.5338 80.66 79.844 80.8193 80.245 80.8193ZM88.8002 81.5803C88.526 81.6557 88.2603 81.7105 88.0033 81.7448C87.7462 81.779 87.4703 81.7962 87.1755 81.7962C86.7505 81.7962 86.4077 81.7208 86.1473 81.57C85.8868 81.4192 85.6948 81.1861 85.5714 80.8707C85.448 80.5554 85.3863 80.1441 85.3863 79.6368V74.8863H83.7823V73.9814H86.4763V79.5854C86.4763 79.8939 86.5037 80.1355 86.5586 80.3103C86.6168 80.4817 86.7248 80.6103 86.8825 80.6959C87.0436 80.7782 87.2783 80.8193 87.5868 80.8193C87.7376 80.8193 87.8987 80.8039 88.0701 80.7731C88.2449 80.7422 88.4471 80.6925 88.6768 80.624L88.8002 81.5803ZM94.9697 78.7525C94.9697 79.0027 94.9595 79.2358 94.9389 79.4517H91.2577C91.3537 79.9042 91.5696 80.2487 91.9055 80.4852C92.2414 80.7217 92.6836 80.8399 93.232 80.8399C93.6536 80.8399 94.0992 80.7919 94.5687 80.6959L94.7024 81.6522C94.1986 81.7482 93.6673 81.7962 93.1086 81.7962C92.4882 81.7962 91.9604 81.6865 91.5251 81.4671C91.0932 81.2478 90.7642 80.9273 90.5379 80.5057C90.3117 80.0807 90.1986 79.5649 90.1986 78.9582C90.1986 78.3686 90.2946 77.8631 90.4865 77.4415C90.6785 77.0199 90.9578 76.6977 91.3246 76.4749C91.6947 76.2521 92.1386 76.1407 92.6562 76.1407C93.3965 76.1407 93.9672 76.3652 94.3682 76.8143C94.7692 77.2633 94.9697 77.9094 94.9697 78.7525ZM93.9826 78.6189C93.9826 78.1321 93.8661 77.7568 93.633 77.4929C93.4034 77.229 93.0812 77.097 92.6664 77.097C92.2449 77.097 91.909 77.229 91.6587 77.4929C91.4085 77.7568 91.2611 78.1321 91.2166 78.6189H93.9826Z"
          fill="#AC9E22"
        />
        <path
          d="M98.7538 81.7962C98.6167 81.7962 98.4898 81.7619 98.3733 81.6934C98.2568 81.6248 98.1642 81.5323 98.0957 81.4157C98.0271 81.2992 97.9928 81.1724 97.9928 81.0353C97.9928 80.8982 98.0271 80.7714 98.0957 80.6548C98.1642 80.5383 98.2568 80.4457 98.3733 80.3772C98.4898 80.3086 98.6167 80.2744 98.7538 80.2744C98.8909 80.2744 99.0177 80.3086 99.1342 80.3772C99.2508 80.4457 99.3433 80.5383 99.4118 80.6548C99.4804 80.7714 99.5147 80.8982 99.5147 81.0353C99.5147 81.1724 99.4804 81.2992 99.4118 81.4157C99.3433 81.5323 99.2508 81.6248 99.1342 81.6934C99.0177 81.7619 98.8909 81.7962 98.7538 81.7962ZM107.309 81.5803C107.035 81.6557 106.769 81.7105 106.512 81.7448C106.255 81.779 105.979 81.7962 105.684 81.7962C105.259 81.7962 104.916 81.7208 104.656 81.57C104.395 81.4192 104.204 81.1861 104.08 80.8707C103.957 80.5554 103.895 80.1441 103.895 79.6368V74.8863H102.291V73.9814H104.985V79.5854C104.985 79.8939 105.012 80.1355 105.067 80.3103C105.126 80.4817 105.234 80.6103 105.391 80.6959C105.552 80.7782 105.787 80.8193 106.096 80.8193C106.246 80.8193 106.407 80.8039 106.579 80.7731C106.754 80.7422 106.956 80.6925 107.186 80.624L107.309 81.5803ZM111.093 81.7962C110.592 81.7962 110.162 81.6865 109.802 81.4671C109.446 81.2444 109.173 80.9222 108.985 80.5006C108.8 80.0756 108.707 79.5649 108.707 78.9685C108.707 78.3721 108.8 77.8631 108.985 77.4415C109.173 77.0165 109.446 76.6943 109.802 76.4749C110.162 76.2521 110.592 76.1407 111.093 76.1407C111.593 76.1407 112.022 76.2521 112.378 76.4749C112.738 76.6943 113.011 77.0165 113.196 77.4415C113.384 77.8631 113.478 78.3721 113.478 78.9685C113.478 79.5649 113.384 80.0756 113.196 80.5006C113.011 80.9222 112.738 81.2444 112.378 81.4671C112.022 81.6865 111.593 81.7962 111.093 81.7962ZM111.093 80.8193C111.494 80.8193 111.802 80.66 112.018 80.3412C112.238 80.019 112.347 79.5614 112.347 78.9685C112.347 78.3755 112.238 77.9196 112.018 77.6009C111.802 77.2787 111.494 77.1176 111.093 77.1176C110.692 77.1176 110.382 77.2787 110.162 77.6009C109.946 77.9196 109.838 78.3755 109.838 78.9685C109.838 79.5614 109.946 80.019 110.162 80.3412C110.382 80.66 110.692 80.8193 111.093 80.8193ZM119.484 76.2436V81.2101C119.484 81.8613 119.386 82.3943 119.19 82.809C118.999 83.2238 118.699 83.5357 118.291 83.7447C117.883 83.9573 117.355 84.0755 116.707 84.0995L115.113 84.1509L115.062 83.1843L116.81 83.1021C117.355 83.0781 117.753 82.9256 118.003 82.6445C118.256 82.3634 118.383 81.9401 118.383 81.3746V80.8502H118.291C118.256 81.1587 118.111 81.3934 117.854 81.5545C117.597 81.7156 117.259 81.7962 116.841 81.7962C116.382 81.7962 115.989 81.6899 115.664 81.4774C115.341 81.2615 115.095 80.9462 114.923 80.5314C114.755 80.1167 114.671 79.6128 114.671 79.0199C114.671 78.4132 114.755 77.8939 114.923 77.4621C115.095 77.0302 115.343 76.7029 115.669 76.4801C115.998 76.2538 116.392 76.1407 116.851 76.1407C117.286 76.1407 117.622 76.2316 117.859 76.4132C118.099 76.5915 118.243 76.864 118.291 77.2307H118.409L118.496 76.2436H119.484ZM117.016 80.8707C117.934 80.8707 118.394 80.5623 118.394 79.9453V78.6291C118.394 77.5872 117.931 77.0662 117.005 77.0662C116.604 77.0662 116.294 77.2358 116.075 77.5752C115.859 77.9145 115.751 78.3961 115.751 79.0199C115.751 79.63 115.857 80.091 116.07 80.4029C116.282 80.7148 116.598 80.8707 117.016 80.8707ZM125.232 82.8244C124.491 82.8244 123.857 82.6359 123.329 82.2589C122.805 81.8819 122.404 81.3335 122.126 80.6137C121.852 79.8939 121.715 79.0267 121.715 78.0122C121.715 77.0113 121.852 76.1562 122.126 75.4467C122.404 74.7337 122.805 74.1922 123.329 73.822C123.857 73.4484 124.491 73.2616 125.232 73.2616H125.478L125.375 74.1356H125.334C124.786 74.1356 124.318 74.2882 123.931 74.5932C123.543 74.8948 123.249 75.337 123.046 75.9197C122.844 76.4989 122.743 77.1964 122.743 78.0122C122.743 78.8416 122.844 79.5511 123.046 80.1407C123.249 80.7302 123.543 81.1792 123.931 81.4877C124.318 81.7962 124.786 81.9504 125.334 81.9504H125.375L125.478 82.8244H125.232ZM127.381 74.3927H128.368L129.293 77.6831H128.1L127.381 74.3927ZM129.643 74.3927H130.63L131.555 77.6831H130.363L129.643 74.3927ZM136.183 76.1407C136.758 76.1407 137.204 76.3155 137.519 76.6652C137.835 77.0148 137.992 77.5083 137.992 78.1459V81.6934H136.902V78.1459C136.902 77.8031 136.817 77.5375 136.645 77.3489C136.474 77.1604 136.234 77.0662 135.925 77.0662C135.069 77.0662 134.64 77.5872 134.64 78.6291V81.6934H133.55V73.9814H134.64V77.2307H134.743C134.774 76.864 134.907 76.5915 135.144 76.4132C135.38 76.2316 135.727 76.1407 136.183 76.1407ZM141.941 81.7962C141.44 81.7962 141.01 81.6865 140.65 81.4671C140.294 81.2444 140.021 80.9222 139.833 80.5006C139.648 80.0756 139.555 79.5649 139.555 78.9685C139.555 78.3721 139.648 77.8631 139.833 77.4415C140.021 77.0165 140.294 76.6943 140.65 76.4749C141.01 76.2521 141.44 76.1407 141.941 76.1407C142.441 76.1407 142.87 76.2521 143.226 76.4749C143.586 76.6943 143.859 77.0165 144.044 77.4415C144.232 77.8631 144.326 78.3721 144.326 78.9685C144.326 79.5649 144.232 80.0756 144.044 80.5006C143.859 80.9222 143.586 81.2444 143.226 81.4671C142.87 81.6865 142.441 81.7962 141.941 81.7962ZM141.941 80.8193C142.342 80.8193 142.65 80.66 142.866 80.3412C143.086 80.019 143.195 79.5614 143.195 78.9685C143.195 78.3755 143.086 77.9196 142.866 77.6009C142.65 77.2787 142.342 77.1176 141.941 77.1176C141.54 77.1176 141.23 77.2787 141.01 77.6009C140.794 77.9196 140.686 78.3755 140.686 78.9685C140.686 79.5614 140.794 80.019 141.01 80.3412C141.23 80.66 141.54 80.8193 141.941 80.8193ZM145.437 76.2436H146.599L148.09 80.8707H148.182L149.622 76.2436H150.784L148.861 81.7962H147.411L145.437 76.2436ZM156.666 78.7525C156.666 79.0027 156.655 79.2358 156.635 79.4517H152.953C153.049 79.9042 153.265 80.2487 153.601 80.4852C153.937 80.7217 154.379 80.8399 154.928 80.8399C155.349 80.8399 155.795 80.7919 156.264 80.6959L156.398 81.6522C155.894 81.7482 155.363 81.7962 154.804 81.7962C154.184 81.7962 153.656 81.6865 153.221 81.4671C152.789 81.2478 152.46 80.9273 152.234 80.5057C152.007 80.0807 151.894 79.5649 151.894 78.9582C151.894 78.3686 151.99 77.8631 152.182 77.4415C152.374 77.0199 152.654 76.6977 153.02 76.4749C153.39 76.2521 153.834 76.1407 154.352 76.1407C155.092 76.1407 155.663 76.3652 156.064 76.8143C156.465 77.2633 156.666 77.9094 156.666 78.7525ZM155.678 78.6189C155.678 78.1321 155.562 77.7568 155.329 77.4929C155.099 77.229 154.777 77.097 154.362 77.097C153.941 77.097 153.605 77.229 153.355 77.4929C153.104 77.7568 152.957 78.1321 152.912 78.6189H155.678ZM161.683 76.1407C162.06 76.1407 162.366 76.2127 162.599 76.3567C162.835 76.4972 163.008 76.7166 163.118 77.0148C163.231 77.313 163.288 77.7037 163.288 78.187H162.208C162.208 77.7928 162.148 77.5083 162.028 77.3335C161.911 77.1553 161.721 77.0662 161.457 77.0662C161.001 77.0662 160.657 77.2615 160.424 77.6523C160.194 78.043 160.079 78.6086 160.079 79.3489V80.7885H161.416V81.6934H157.776V80.7885H158.989V77.1484H157.982V76.2436H159.874L160.059 77.642H160.182C160.213 77.1382 160.352 76.7628 160.599 76.5161C160.849 76.2658 161.21 76.1407 161.683 76.1407ZM167.647 74.3927H168.84L167.915 77.6831H166.928L167.647 74.3927ZM165.385 74.3927H166.578L165.653 77.6831H164.665L165.385 74.3927ZM170.886 81.9504C171.435 81.9504 171.903 81.7962 172.29 81.4877C172.677 81.1792 172.972 80.7302 173.174 80.1407C173.376 79.5511 173.478 78.8416 173.478 78.0122C173.478 77.1964 173.376 76.4989 173.174 75.9197C172.972 75.337 172.677 74.8948 172.29 74.5932C171.903 74.2882 171.435 74.1356 170.886 74.1356H170.845L170.742 73.2616H170.989C171.73 73.2616 172.362 73.4484 172.886 73.822C173.414 74.1922 173.815 74.7337 174.089 75.4467C174.367 76.1562 174.506 77.0113 174.506 78.0122C174.506 79.0267 174.367 79.8939 174.089 80.6137C173.815 81.3335 173.414 81.8819 172.886 82.2589C172.362 82.6359 171.73 82.8244 170.989 82.8244H170.742L170.845 81.9504H170.886ZM178.958 77.6626C178.821 77.6626 178.694 77.6283 178.578 77.5597C178.461 77.4912 178.369 77.3986 178.3 77.2821C178.232 77.1656 178.197 77.0388 178.197 76.9017C178.197 76.7646 178.232 76.6377 178.3 76.5212C178.369 76.4047 178.461 76.3121 178.578 76.2436C178.694 76.175 178.821 76.1407 178.958 76.1407C179.095 76.1407 179.222 76.175 179.339 76.2436C179.455 76.3121 179.548 76.4047 179.616 76.5212C179.685 76.6377 179.719 76.7646 179.719 76.9017C179.719 77.0388 179.685 77.1656 179.616 77.2821C179.548 77.3986 179.455 77.4912 179.339 77.5597C179.222 77.6283 179.095 77.6626 178.958 77.6626ZM179.544 80.3875L178.927 83.6779H177.94L178.352 80.3875H179.544Z"
          fill="#737373"
        />
      </g>
      <g opacity="0.6">
        <path
          d="M56.8006 92.9864C57.1777 92.9864 57.4827 93.0584 57.7158 93.2024C57.9523 93.3429 58.1254 93.5623 58.2351 93.8605C58.3482 94.1587 58.4047 94.5494 58.4047 95.0327H57.3251C57.3251 94.6385 57.2651 94.354 57.1451 94.1792C57.0286 94.001 56.8383 93.9119 56.5744 93.9119C56.1186 93.9119 55.7741 94.1073 55.541 94.498C55.3114 94.8887 55.1965 95.4543 55.1965 96.1946V97.6342H56.5333V98.5391H52.8932V97.6342H54.1066V93.9941H53.0989V93.0893H54.9909L55.176 94.4877H55.2994C55.3302 93.9839 55.469 93.6085 55.7158 93.3618C55.966 93.1115 56.3276 92.9864 56.8006 92.9864ZM64.1219 95.5982C64.1219 95.8484 64.1116 96.0815 64.091 96.2974H60.4098C60.5058 96.7499 60.7217 97.0944 61.0576 97.3309C61.3935 97.5674 61.8357 97.6856 62.3841 97.6856C62.8057 97.6856 63.2513 97.6376 63.7208 97.5416L63.8545 98.4979C63.3507 98.5939 62.8194 98.6419 62.2607 98.6419C61.6403 98.6419 61.1125 98.5322 60.6772 98.3128C60.2453 98.0935 59.9163 97.773 59.6901 97.3514C59.4638 96.9264 59.3507 96.4106 59.3507 95.8039C59.3507 95.2143 59.4467 94.7088 59.6386 94.2872C59.8306 93.8656 60.1099 93.5434 60.4767 93.3206C60.8469 93.0978 61.2907 92.9864 61.8083 92.9864C62.5486 92.9864 63.1193 93.2109 63.5203 93.66C63.9214 94.109 64.1219 94.7551 64.1219 95.5982ZM63.1347 95.4646C63.1347 94.9778 63.0182 94.6025 62.7851 94.3386C62.5555 94.0747 62.2333 93.9427 61.8186 93.9427C61.397 93.9427 61.0611 94.0747 60.8109 94.3386C60.5607 94.6025 60.4133 94.9778 60.3687 95.4646H63.1347ZM70.2709 98.5185C69.7876 98.6008 69.3112 98.6419 68.8416 98.6419C68.2863 98.6419 67.8373 98.5665 67.4946 98.4157C67.1518 98.2649 66.8999 98.0318 66.7388 97.7165C66.5777 97.4011 66.4972 96.9898 66.4972 96.4825V93.9941H65.1296V93.0893H66.4972V91.444H67.5871V93.0893H70.0652V93.9941H67.5871V96.4311C67.5871 96.8664 67.6934 97.1818 67.9059 97.3771C68.1184 97.5691 68.4646 97.665 68.9444 97.665C69.1021 97.665 69.27 97.6565 69.4483 97.6393C69.6299 97.6222 69.863 97.5965 70.1475 97.5622L70.2709 98.5185ZM76.9443 97.665V98.5391L75.1963 98.5905L75.1191 97.4902H75.0215C74.9392 97.8604 74.7815 98.1449 74.5485 98.3437C74.3188 98.5425 74.0343 98.6419 73.695 98.6419C73.0883 98.6419 72.6187 98.4722 72.2863 98.1329C71.9572 97.7936 71.7927 97.312 71.7927 96.6882V92.9864H72.8621V96.6882C72.8621 97.0172 72.9529 97.2709 73.1346 97.4491C73.3162 97.6273 73.5716 97.7165 73.9006 97.7165C74.3051 97.7165 74.6067 97.5845 74.8055 97.3206C75.0043 97.0532 75.1037 96.6196 75.1037 96.0198V92.9864H76.1937V97.5519L76.9443 97.665ZM81.4789 92.9864C81.856 92.9864 82.161 93.0584 82.3941 93.2024C82.6306 93.3429 82.8037 93.5623 82.9134 93.8605C83.0265 94.1587 83.083 94.5494 83.083 95.0327H82.0034C82.0034 94.6385 81.9434 94.354 81.8234 94.1792C81.7069 94.001 81.5166 93.9119 81.2527 93.9119C80.7969 93.9119 80.4524 94.1073 80.2193 94.498C79.9897 94.8887 79.8749 95.4543 79.8749 96.1946V97.6342H81.2116V98.5391H77.5715V97.6342H78.7849V93.9941H77.7772V93.0893H79.6692L79.8543 94.4877H79.9777C80.0085 93.9839 80.1473 93.6085 80.3941 93.3618C80.6443 93.1115 81.0059 92.9864 81.4789 92.9864ZM86.8259 92.9864C87.4017 92.9864 87.8473 93.1612 88.1626 93.5109C88.478 93.8605 88.6357 94.354 88.6357 94.9916V98.5391H87.5457V94.9916C87.5457 94.6488 87.46 94.3832 87.2886 94.1947C87.1172 94.0061 86.8773 93.9119 86.5688 93.9119C85.712 93.9119 85.2835 94.4329 85.2835 95.4748V98.5391H84.1936V93.0893H85.1807L85.2681 94.0764H85.3863C85.4172 93.7097 85.5509 93.4372 85.7874 93.2589C86.0239 93.0773 86.37 92.9864 86.8259 92.9864ZM123.432 94.5083C123.295 94.5083 123.168 94.474 123.052 94.4054C122.935 94.3369 122.843 94.2444 122.774 94.1278C122.705 94.0113 122.671 93.8845 122.671 93.7474C122.671 93.6103 122.705 93.4834 122.774 93.3669C122.843 93.2504 122.935 93.1578 123.052 93.0893C123.168 93.0207 123.295 92.9864 123.432 92.9864C123.569 92.9864 123.696 93.0207 123.813 93.0893C123.929 93.1578 124.022 93.2504 124.09 93.3669C124.159 93.4834 124.193 93.6103 124.193 93.7474C124.193 93.8845 124.159 94.0113 124.09 94.1278C124.022 94.2444 123.929 94.3369 123.813 94.4054C123.696 94.474 123.569 94.5083 123.432 94.5083ZM124.018 97.2332L123.401 100.524H122.414L122.825 97.2332H124.018Z"
          fill="#737373"
        />
        <path
          d="M101.119 98.5185C100.635 98.6008 100.159 98.6419 99.6895 98.6419C99.1342 98.6419 98.6852 98.5665 98.3425 98.4157C97.9997 98.2649 97.7478 98.0318 97.5867 97.7165C97.4256 97.4011 97.345 96.9898 97.345 96.4825V93.9941H95.9774V93.0893H97.345V91.444H98.435V93.0893H100.913V93.9941H98.435V96.4311C98.435 96.8664 98.5412 97.1818 98.7538 97.3771C98.9663 97.5691 99.3124 97.665 99.7923 97.665C99.95 97.665 100.118 97.6565 100.296 97.6393C100.478 97.6222 100.711 97.5965 100.995 97.5622L101.119 98.5185ZM106.157 92.9864C106.534 92.9864 106.839 93.0584 107.072 93.2024C107.309 93.3429 107.482 93.5623 107.592 93.8605C107.705 94.1587 107.761 94.5494 107.761 95.0327H106.682C106.682 94.6385 106.622 94.354 106.502 94.1792C106.385 94.001 106.195 93.9119 105.931 93.9119C105.475 93.9119 105.131 94.1073 104.898 94.498C104.668 94.8887 104.553 95.4543 104.553 96.1946V97.6342H105.89V98.5391H102.25V97.6342H103.463V93.9941H102.456V93.0893H104.348L104.533 94.4877H104.656C104.687 93.9839 104.826 93.6085 105.072 93.3618C105.323 93.1115 105.684 92.9864 106.157 92.9864ZM113.962 97.665V98.5391L112.214 98.5905L112.137 97.4902H112.039C111.957 97.8604 111.799 98.1449 111.566 98.3437C111.336 98.5425 111.052 98.6419 110.712 98.6419C110.106 98.6419 109.636 98.4722 109.304 98.1329C108.975 97.7936 108.81 97.312 108.81 96.6882V92.9864H109.88V96.6882C109.88 97.0172 109.97 97.2709 110.152 97.4491C110.334 97.6273 110.589 97.7165 110.918 97.7165C111.323 97.7165 111.624 97.5845 111.823 97.3206C112.022 97.0532 112.121 96.6196 112.121 96.0198V92.9864H113.211V97.5519L113.962 97.665ZM119.648 95.5982C119.648 95.8484 119.638 96.0815 119.617 96.2974H115.936C116.032 96.7499 116.248 97.0944 116.584 97.3309C116.92 97.5674 117.362 97.6856 117.91 97.6856C118.332 97.6856 118.777 97.6376 119.247 97.5416L119.381 98.4979C118.877 98.5939 118.346 98.6419 117.787 98.6419C117.167 98.6419 116.639 98.5322 116.203 98.3128C115.772 98.0935 115.442 97.773 115.216 97.3514C114.99 96.9264 114.877 96.4106 114.877 95.8039C114.877 95.2143 114.973 94.7088 115.165 94.2872C115.357 93.8656 115.636 93.5434 116.003 93.3206C116.373 93.0978 116.817 92.9864 117.334 92.9864C118.075 92.9864 118.645 93.2109 119.047 93.66C119.448 94.109 119.648 94.7551 119.648 95.5982ZM118.661 95.4646C118.661 94.9778 118.544 94.6025 118.311 94.3386C118.082 94.0747 117.759 93.9427 117.345 93.9427C116.923 93.9427 116.587 94.0747 116.337 94.3386C116.087 94.6025 115.939 94.9778 115.895 95.4646H118.661Z"
          fill="#544BA3"
        />
      </g>
      <path
        opacity="0.6"
        d="M54.2505 116.538C53.9763 116.538 53.7364 116.504 53.5308 116.436L53.6542 115.562C53.8598 115.63 54.0278 115.664 54.158 115.664C54.4493 115.664 54.6721 115.544 54.8264 115.304C54.984 115.065 55.0629 114.722 55.0629 114.276C55.0629 114.033 55.0577 113.793 55.0474 113.556C55.044 113.478 55.0406 113.359 55.0372 113.202C55.0337 113.044 55.032 112.902 55.032 112.775C55.032 112.477 55.1143 112.242 55.2788 112.071C55.4433 111.896 55.673 111.805 55.9677 111.798V111.623C55.6695 111.616 55.4382 111.529 55.2737 111.361C55.1126 111.19 55.032 110.951 55.032 110.646C55.032 110.526 55.0337 110.391 55.0372 110.24C55.0406 110.089 55.044 109.975 55.0474 109.896C55.0577 109.649 55.0629 109.399 55.0629 109.145C55.0629 108.73 54.984 108.412 54.8264 108.189C54.6721 107.963 54.4493 107.85 54.158 107.85C54.0278 107.85 53.8598 107.884 53.6542 107.952L53.5308 107.078C53.7364 107.01 53.9763 106.976 54.2505 106.976C54.8572 106.976 55.3268 107.164 55.6593 107.541C55.9952 107.918 56.1631 108.453 56.1631 109.145C56.1631 109.447 56.1614 109.673 56.158 109.824L56.1528 110.564C56.1528 110.712 56.2197 110.842 56.3533 110.955C56.487 111.065 56.6464 111.15 56.8315 111.212C57.02 111.274 57.1879 111.305 57.3353 111.305V112.117C57.1708 112.117 56.9977 112.141 56.8161 112.189C56.6344 112.237 56.4784 112.316 56.3482 112.425C56.218 112.535 56.1528 112.679 56.1528 112.857L56.158 113.582C56.1614 113.736 56.1631 113.968 56.1631 114.276C56.1631 114.749 56.0877 115.155 55.9369 115.495C55.7861 115.834 55.5667 116.093 55.2788 116.271C54.9943 116.449 54.6516 116.538 54.2505 116.538ZM70.2914 112.446C70.2914 112.696 70.2812 112.929 70.2606 113.145H66.5794C66.6754 113.598 66.8913 113.942 67.2272 114.179C67.5631 114.415 68.0053 114.533 68.5537 114.533C68.9753 114.533 69.4208 114.485 69.8904 114.389L70.0241 115.346C69.5202 115.442 68.989 115.49 68.4303 115.49C67.8099 115.49 67.2821 115.38 66.8468 115.161C66.4149 114.941 66.0858 114.621 65.8596 114.199C65.6334 113.774 65.5203 113.258 65.5203 112.652C65.5203 112.062 65.6163 111.556 65.8082 111.135C66.0002 110.713 66.2795 110.391 66.6463 110.168C67.0164 109.945 67.4603 109.834 67.9779 109.834C68.7182 109.834 69.2889 110.059 69.6899 110.508C70.0909 110.957 70.2914 111.603 70.2914 112.446ZM69.3043 112.312C69.3043 111.826 69.1878 111.45 68.9547 111.186C68.7251 110.922 68.4029 110.79 67.9881 110.79C67.5665 110.79 67.2306 110.922 66.9804 111.186C66.7302 111.45 66.5828 111.826 66.5383 112.312H69.3043ZM76.461 115.274C76.1868 115.349 75.9212 115.404 75.6641 115.438C75.4071 115.472 75.1311 115.49 74.8364 115.49C74.4113 115.49 74.0686 115.414 73.8081 115.263C73.5476 115.113 73.3557 114.879 73.2323 114.564C73.1089 114.249 73.0472 113.837 73.0472 113.33V108.58H71.4431V107.675H74.1371V113.279C74.1371 113.587 74.1646 113.829 74.2194 114.004C74.2777 114.175 74.3856 114.304 74.5433 114.389C74.7044 114.472 74.9392 114.513 75.2477 114.513C75.3985 114.513 75.5596 114.497 75.731 114.466C75.9058 114.436 76.108 114.386 76.3376 114.317L76.461 115.274ZM79.8234 115.49C79.4053 115.49 79.0488 115.478 78.754 115.454C78.4627 115.43 78.1988 115.39 77.9623 115.335L78.0857 114.348C78.7849 114.458 79.3641 114.513 79.8234 114.513C80.3718 114.513 80.7712 114.46 81.0214 114.353C81.2716 114.244 81.3967 114.071 81.3967 113.834C81.3967 113.676 81.3538 113.556 81.2681 113.474C81.1859 113.392 81.0505 113.327 80.862 113.279L79.6075 112.96C79.2579 112.871 78.9785 112.768 78.7695 112.652C78.5604 112.535 78.4044 112.388 78.3016 112.209C78.1988 112.028 78.1474 111.802 78.1474 111.531C78.1474 111.13 78.2433 110.806 78.4353 110.559C78.6307 110.309 78.9323 110.125 79.3402 110.009C79.7515 109.892 80.2862 109.834 80.9442 109.834C81.4447 109.834 81.9245 109.868 82.3838 109.937L82.2707 110.883C81.7531 110.835 81.3007 110.811 80.9134 110.811C80.3376 110.811 79.9211 110.864 79.6641 110.97C79.407 111.073 79.2785 111.243 79.2785 111.479C79.2785 111.63 79.323 111.743 79.4121 111.819C79.5012 111.894 79.6521 111.959 79.8646 112.014L80.8723 112.261C81.263 112.357 81.5783 112.47 81.8183 112.6C82.0582 112.73 82.2364 112.893 82.353 113.089C82.4695 113.284 82.5278 113.526 82.5278 113.813C82.5278 114.208 82.4335 114.528 82.245 114.775C82.0565 115.018 81.7634 115.198 81.3658 115.315C80.9717 115.431 80.4575 115.49 79.8234 115.49ZM88.8002 112.446C88.8002 112.696 88.7899 112.929 88.7693 113.145H85.0881C85.1841 113.598 85.4001 113.942 85.7359 114.179C86.0718 114.415 86.514 114.533 87.0624 114.533C87.484 114.533 87.9296 114.485 88.3992 114.389L88.5328 115.346C88.029 115.442 87.4977 115.49 86.939 115.49C86.3186 115.49 85.7908 115.38 85.3555 115.161C84.9236 114.941 84.5946 114.621 84.3684 114.199C84.1421 113.774 84.029 113.258 84.029 112.652C84.029 112.062 84.125 111.556 84.3169 111.135C84.5089 110.713 84.7882 110.391 85.155 110.168C85.5252 109.945 85.969 109.834 86.4866 109.834C87.2269 109.834 87.7976 110.059 88.1986 110.508C88.5997 110.957 88.8002 111.603 88.8002 112.446ZM87.813 112.312C87.813 111.826 87.6965 111.45 87.4634 111.186C87.2338 110.922 86.9116 110.79 86.4969 110.79C86.0753 110.79 85.7394 110.922 85.4892 111.186C85.239 111.45 85.0916 111.826 85.047 112.312H87.813ZM100.07 116.538C99.6689 116.538 99.3244 116.449 99.0365 116.271C98.752 116.093 98.5344 115.834 98.3836 115.495C98.2328 115.155 98.1574 114.749 98.1574 114.276C98.1574 113.968 98.1591 113.736 98.1625 113.582L98.1676 112.857C98.1676 112.679 98.1025 112.535 97.9723 112.425C97.842 112.316 97.6861 112.237 97.5044 112.189C97.3228 112.141 97.1497 112.117 96.9851 112.117V111.305C97.1325 111.305 97.2988 111.274 97.4839 111.212C97.6724 111.15 97.8335 111.065 97.9671 110.955C98.1008 110.842 98.1676 110.712 98.1676 110.564L98.1625 109.824C98.1591 109.673 98.1574 109.447 98.1574 109.145C98.1574 108.453 98.3236 107.918 98.6561 107.541C98.992 107.164 99.4633 106.976 100.07 106.976C100.344 106.976 100.584 107.01 100.79 107.078L100.666 107.952C100.461 107.884 100.293 107.85 100.162 107.85C99.8711 107.85 99.6466 107.963 99.489 108.189C99.3347 108.412 99.2576 108.73 99.2576 109.145C99.2576 109.262 99.2593 109.395 99.2627 109.546C99.2662 109.697 99.2696 109.814 99.273 109.896C99.2833 110.136 99.2885 110.386 99.2885 110.646C99.2885 110.951 99.2062 111.19 99.0417 111.361C98.8806 111.529 98.6509 111.616 98.3527 111.623V111.798C98.6475 111.805 98.8771 111.896 99.0417 112.071C99.2062 112.242 99.2885 112.477 99.2885 112.775C99.2885 112.902 99.2867 113.044 99.2833 113.202C99.2799 113.359 99.2765 113.478 99.273 113.556C99.2627 113.793 99.2576 114.033 99.2576 114.276C99.2576 114.722 99.3347 115.065 99.489 115.304C99.6466 115.544 99.8711 115.664 100.162 115.664C100.293 115.664 100.461 115.63 100.666 115.562L100.79 116.436C100.584 116.504 100.344 116.538 100.07 116.538Z"
        fill="#737373"
      />
      <g opacity="0.6">
        <path
          d="M69.4364 128.787C69.8134 128.787 70.1185 128.859 70.3515 129.003C70.588 129.144 70.7611 129.363 70.8708 129.661C70.9839 129.959 71.0405 130.35 71.0405 130.833H69.9608C69.9608 130.439 69.9008 130.155 69.7808 129.98C69.6643 129.802 69.4741 129.713 69.2102 129.713C68.7543 129.713 68.4098 129.908 68.1768 130.299C67.9471 130.69 67.8323 131.255 67.8323 131.995V133.435H69.169V134.34H65.529V133.435H66.7423V129.795H65.7346V128.89H67.6266L67.8117 130.288H67.9351C67.966 129.785 68.1048 129.409 68.3516 129.163C68.6018 128.912 68.9634 128.787 69.4364 128.787ZM76.7576 131.399C76.7576 131.649 76.7473 131.882 76.7268 132.098H73.0456C73.1416 132.551 73.3575 132.895 73.6934 133.132C74.0293 133.368 74.4714 133.486 75.0198 133.486C75.4414 133.486 75.887 133.438 76.3566 133.342L76.4903 134.299C75.9864 134.395 75.4551 134.443 74.8965 134.443C74.2761 134.443 73.7482 134.333 73.3129 134.114C72.8811 133.894 72.552 133.574 72.3258 133.152C72.0996 132.727 71.9865 132.211 71.9865 131.605C71.9865 131.015 72.0824 130.51 72.2744 130.088C72.4663 129.666 72.7457 129.344 73.1124 129.121C73.4826 128.899 73.9265 128.787 74.444 128.787C75.1844 128.787 75.7551 129.012 76.1561 129.461C76.5571 129.91 76.7576 130.556 76.7576 131.399ZM75.7705 131.265C75.7705 130.779 75.6539 130.403 75.4209 130.139C75.1912 129.875 74.869 129.744 74.4543 129.744C74.0327 129.744 73.6968 129.875 73.4466 130.139C73.1964 130.403 73.049 130.779 73.0045 131.265H75.7705ZM82.9066 134.319C82.4233 134.402 81.9469 134.443 81.4773 134.443C80.9221 134.443 80.4731 134.367 80.1303 134.216C79.7876 134.066 79.5356 133.833 79.3745 133.517C79.2134 133.202 79.1329 132.791 79.1329 132.283V129.795H77.7653V128.89H79.1329V127.245H80.2229V128.89H82.701V129.795H80.2229V132.232C80.2229 132.667 80.3291 132.983 80.5416 133.178C80.7541 133.37 81.1003 133.466 81.5802 133.466C81.7378 133.466 81.9058 133.457 82.084 133.44C82.2657 133.423 82.4987 133.397 82.7832 133.363L82.9066 134.319ZM89.58 133.466V134.34L87.832 134.391L87.7549 133.291H87.6572C87.5749 133.661 87.4173 133.946 87.1842 134.144C86.9545 134.343 86.6701 134.443 86.3307 134.443C85.7241 134.443 85.2545 134.273 84.922 133.934C84.593 133.594 84.4284 133.113 84.4284 132.489V128.787H85.4978V132.489C85.4978 132.818 85.5887 133.072 85.7703 133.25C85.952 133.428 86.2073 133.517 86.5364 133.517C86.9408 133.517 87.2425 133.385 87.4413 133.121C87.6401 132.854 87.7395 132.42 87.7395 131.821V128.787H88.8294V133.353L89.58 133.466ZM94.1147 128.787C94.4917 128.787 94.7968 128.859 95.0298 129.003C95.2663 129.144 95.4394 129.363 95.5491 129.661C95.6622 129.959 95.7188 130.35 95.7188 130.833H94.6391C94.6391 130.439 94.5791 130.155 94.4592 129.98C94.3426 129.802 94.1524 129.713 93.8885 129.713C93.4326 129.713 93.0881 129.908 92.8551 130.299C92.6254 130.69 92.5106 131.255 92.5106 131.995V133.435H93.8473V134.34H90.2073V133.435H91.4206V129.795H90.4129V128.89H92.3049L92.49 130.288H92.6134C92.6443 129.785 92.7831 129.409 93.0299 129.163C93.2801 128.912 93.6417 128.787 94.1147 128.787ZM99.4617 128.787C100.037 128.787 100.483 128.962 100.798 129.312C101.114 129.661 101.271 130.155 101.271 130.792V134.34H100.181V130.792C100.181 130.45 100.096 130.184 99.9244 129.995C99.753 129.807 99.5131 129.713 99.2046 129.713C98.3477 129.713 97.9193 130.234 97.9193 131.276V134.34H96.8293V128.89H97.8164L97.9038 129.877H98.0221C98.0529 129.51 98.1866 129.238 98.4231 129.06C98.6596 128.878 99.0058 128.787 99.4617 128.787ZM142.237 130.309C142.1 130.309 141.973 130.275 141.857 130.206C141.74 130.138 141.648 130.045 141.579 129.929C141.511 129.812 141.476 129.685 141.476 129.548C141.476 129.411 141.511 129.284 141.579 129.168C141.648 129.051 141.74 128.959 141.857 128.89C141.973 128.822 142.1 128.787 142.237 128.787C142.374 128.787 142.501 128.822 142.618 128.89C142.734 128.959 142.827 129.051 142.895 129.168C142.964 129.284 142.998 129.411 142.998 129.548C142.998 129.685 142.964 129.812 142.895 129.929C142.827 130.045 142.734 130.138 142.618 130.206C142.501 130.275 142.374 130.309 142.237 130.309ZM142.823 133.034L142.207 136.324H141.219L141.631 133.034H142.823ZM153.26 135.491C152.986 135.491 152.746 135.457 152.541 135.389L152.664 134.515C152.87 134.583 153.038 134.617 153.168 134.617C153.459 134.617 153.682 134.498 153.836 134.258C153.994 134.018 154.073 133.675 154.073 133.229C154.073 132.986 154.068 132.746 154.057 132.51C154.054 132.431 154.05 132.312 154.047 132.155C154.044 131.997 154.042 131.855 154.042 131.728C154.042 131.43 154.124 131.195 154.289 131.024C154.453 130.849 154.683 130.758 154.978 130.751V130.576C154.679 130.57 154.448 130.482 154.283 130.314C154.122 130.143 154.042 129.905 154.042 129.6C154.042 129.48 154.044 129.344 154.047 129.193C154.05 129.043 154.054 128.928 154.057 128.849C154.068 128.602 154.073 128.352 154.073 128.098C154.073 127.684 153.994 127.365 153.836 127.142C153.682 126.916 153.459 126.803 153.168 126.803C153.038 126.803 152.87 126.837 152.664 126.906L152.541 126.031C152.746 125.963 152.986 125.929 153.26 125.929C153.867 125.929 154.337 126.117 154.669 126.494C155.005 126.871 155.173 127.406 155.173 128.098C155.173 128.4 155.171 128.626 155.168 128.777L155.163 129.517C155.163 129.665 155.229 129.795 155.363 129.908C155.497 130.018 155.656 130.103 155.841 130.165C156.03 130.227 156.198 130.258 156.345 130.258V131.07C156.181 131.07 156.008 131.094 155.826 131.142C155.644 131.19 155.488 131.269 155.358 131.378C155.228 131.488 155.163 131.632 155.163 131.81L155.168 132.535C155.171 132.689 155.173 132.921 155.173 133.229C155.173 133.702 155.098 134.108 154.947 134.448C154.796 134.787 154.577 135.046 154.289 135.224C154.004 135.402 153.661 135.491 153.26 135.491Z"
          fill="#737373"
        />
        <path
          d="M112.922 127.502C111.948 127.502 111.461 128.102 111.461 129.301V130.124H113.693V131.029H111.461V134.34H110.372V131.029H108.654V130.124H110.372V129.353C110.372 128.407 110.586 127.699 111.014 127.229C111.443 126.76 112.089 126.525 112.952 126.525C113.343 126.525 113.72 126.559 114.084 126.628L113.97 127.574C113.566 127.526 113.216 127.502 112.922 127.502ZM120.428 133.466V134.34L118.68 134.391L118.628 133.188H118.52C118.483 133.599 118.308 133.911 117.996 134.124C117.688 134.336 117.275 134.443 116.757 134.443C116.188 134.443 115.748 134.307 115.436 134.037C115.124 133.762 114.968 133.373 114.968 132.869C114.968 132.294 115.177 131.851 115.595 131.543C116.017 131.234 116.613 131.08 117.384 131.08C117.802 131.08 118.162 131.123 118.464 131.209V130.916C118.464 130.546 118.337 130.27 118.083 130.088C117.833 129.906 117.453 129.802 116.942 129.774L115.636 129.713L115.739 128.787L116.839 128.828C117.737 128.863 118.406 129.067 118.844 129.44C119.283 129.814 119.502 130.36 119.502 131.08V133.353L120.428 133.466ZM116.932 133.569C117.254 133.569 117.53 133.512 117.76 133.399C117.993 133.286 118.167 133.138 118.284 132.957C118.404 132.775 118.464 132.585 118.464 132.386V132.057C118.293 132.013 118.121 131.985 117.95 131.975C117.782 131.961 117.569 131.954 117.312 131.954C116.88 131.954 116.553 132.023 116.33 132.16C116.108 132.297 115.996 132.499 115.996 132.767C115.996 133.031 116.075 133.231 116.233 133.368C116.39 133.502 116.623 133.569 116.932 133.569ZM126.114 134.227C125.84 134.302 125.574 134.357 125.317 134.391C125.06 134.426 124.784 134.443 124.49 134.443C124.065 134.443 123.722 134.367 123.461 134.216C123.201 134.066 123.009 133.833 122.885 133.517C122.762 133.202 122.7 132.791 122.7 132.283V127.533H121.096V126.628H123.79V132.232C123.79 132.54 123.818 132.782 123.873 132.957C123.931 133.128 124.039 133.257 124.197 133.342C124.358 133.425 124.592 133.466 124.901 133.466C125.052 133.466 125.213 133.45 125.384 133.42C125.559 133.389 125.761 133.339 125.991 133.27L126.114 134.227ZM129.477 134.443C129.058 134.443 128.702 134.431 128.407 134.407C128.116 134.383 127.852 134.343 127.615 134.288L127.739 133.301C128.438 133.411 129.017 133.466 129.477 133.466C130.025 133.466 130.424 133.413 130.675 133.306C130.925 133.197 131.05 133.024 131.05 132.787C131.05 132.63 131.007 132.51 130.921 132.427C130.839 132.345 130.704 132.28 130.515 132.232L129.261 131.913C128.911 131.824 128.632 131.721 128.423 131.605C128.214 131.488 128.058 131.341 127.955 131.163C127.852 130.981 127.801 130.755 127.801 130.484C127.801 130.083 127.897 129.759 128.088 129.512C128.284 129.262 128.585 129.079 128.993 128.962C129.405 128.845 129.939 128.787 130.597 128.787C131.098 128.787 131.578 128.822 132.037 128.89L131.924 129.836C131.406 129.788 130.954 129.764 130.567 129.764C129.991 129.764 129.574 129.817 129.317 129.923C129.06 130.026 128.932 130.196 128.932 130.432C128.932 130.583 128.976 130.696 129.065 130.772C129.154 130.847 129.305 130.912 129.518 130.967L130.525 131.214C130.916 131.31 131.232 131.423 131.471 131.553C131.711 131.683 131.89 131.846 132.006 132.042C132.123 132.237 132.181 132.479 132.181 132.767C132.181 133.161 132.087 133.481 131.898 133.728C131.71 133.971 131.417 134.151 131.019 134.268C130.625 134.384 130.111 134.443 129.477 134.443ZM138.453 131.399C138.453 131.649 138.443 131.882 138.423 132.098H134.741C134.837 132.551 135.053 132.895 135.389 133.132C135.725 133.368 136.167 133.486 136.716 133.486C137.137 133.486 137.583 133.438 138.052 133.342L138.186 134.299C137.682 134.395 137.151 134.443 136.592 134.443C135.972 134.443 135.444 134.333 135.009 134.114C134.577 133.894 134.248 133.574 134.022 133.152C133.795 132.727 133.682 132.211 133.682 131.605C133.682 131.015 133.778 130.51 133.97 130.088C134.162 129.666 134.441 129.344 134.808 129.121C135.178 128.899 135.622 128.787 136.14 128.787C136.88 128.787 137.451 129.012 137.852 129.461C138.253 129.91 138.453 130.556 138.453 131.399ZM137.466 131.265C137.466 130.779 137.35 130.403 137.117 130.139C136.887 129.875 136.565 129.744 136.15 129.744C135.728 129.744 135.393 129.875 135.142 130.139C134.892 130.403 134.745 130.779 134.7 131.265H137.466Z"
          fill="#544BA3"
        />
      </g>
    </motion.g>
    <motion.circle
      cx="112.5"
      cy="151.5"
      r="4.5"
      fill="#FFEFBF"
      stroke="white"
      strokeWidth="2"
      animate={{ r: [4.5, 5, 4.5] }}
      transition={{
        duration: 1,
        ease: "easeInOut",
        loop: Infinity,
      }}
    />
    <motion.path
      d="M112.5 156V171"
      stroke="white"
      strokeOpacity="0.8"
      strokeDasharray="3 3"
      animate={{
        strokeDashoffset: [6, 0],
      }}
      transition={{
        duration: 1,
        ease: "linear",
        repeat: Infinity,
      }}
    />

    <motion.g
      animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
      transition={{
        duration: 4,
        ease: "easeInOut",
        loop: Infinity,
        times: [1, 0.5, 0],
        repeat: Infinity,
        delay: 1.5,
      }}
    >
      <g filter="url(#filter1_d_680_1635)">
        <rect x="23" y="172" width="179" height="42" rx="11.043" fill="white" />
        <rect
          x="23.25"
          y="172.25"
          width="178.5"
          height="41.5"
          rx="10.793"
          stroke="#F2F2F2"
          stroke-width="0.5"
        />
      </g>
      <path
        d="M61.5912 189.699C62.7086 189.699 63.5449 190.02 64.1002 190.661C64.6554 191.302 64.9331 192.267 64.9331 193.555C64.9331 194.707 64.6554 195.569 64.1002 196.141C63.5449 196.714 62.7086 197 61.5912 197H59.8226V189.699H61.5912ZM61.5912 196.023C62.3419 196.023 62.9057 195.809 63.2827 195.38C63.6632 194.952 63.8534 194.309 63.8534 193.452C63.8534 191.602 63.0993 190.676 61.5912 190.676H60.8509V196.023H61.5912ZM70.6399 194.059C70.6399 194.309 70.6297 194.542 70.6091 194.758H66.9279C67.0239 195.211 67.2398 195.555 67.5757 195.792C67.9116 196.028 68.3538 196.147 68.9022 196.147C69.3238 196.147 69.7693 196.099 70.2389 196.003L70.3726 196.959C69.8687 197.055 69.3375 197.103 68.7788 197.103C68.1584 197.103 67.6306 196.993 67.1953 196.774C66.7634 196.554 66.4343 196.234 66.2081 195.812C65.9819 195.387 65.8688 194.871 65.8688 194.265C65.8688 193.675 65.9648 193.17 66.1567 192.748C66.3487 192.327 66.628 192.004 66.9947 191.782C67.3649 191.559 67.8088 191.447 68.3263 191.447C69.0667 191.447 69.6374 191.672 70.0384 192.121C70.4394 192.57 70.6399 193.216 70.6399 194.059ZM69.6528 193.925C69.6528 193.439 69.5363 193.063 69.3032 192.8C69.0735 192.536 68.7514 192.404 68.3366 192.404C67.915 192.404 67.5791 192.536 67.3289 192.8C67.0787 193.063 66.9313 193.439 66.8868 193.925H69.6528ZM74.8352 191.447C75.2945 191.447 75.687 191.56 76.0126 191.787C76.3417 192.009 76.5901 192.337 76.7581 192.769C76.9295 193.201 77.0152 193.72 77.0152 194.327C77.0152 194.919 76.9295 195.423 76.7581 195.838C76.5901 196.253 76.3434 196.568 76.0177 196.784C75.6956 196.997 75.3048 197.103 74.8455 197.103C74.4274 197.103 74.0898 197.022 73.8327 196.861C73.5756 196.7 73.43 196.465 73.3957 196.157H73.2929V199.365H72.2029V191.55H73.19L73.2774 192.537H73.3957C73.4437 192.171 73.5859 191.898 73.8224 191.72C74.0623 191.538 74.3999 191.447 74.8352 191.447ZM74.6707 196.177C75.0889 196.177 75.4042 196.021 75.6167 195.71C75.8292 195.398 75.9355 194.937 75.9355 194.327C75.9355 193.703 75.8258 193.221 75.6064 192.882C75.3905 192.542 75.082 192.373 74.681 192.373C73.7556 192.373 73.2929 192.894 73.2929 193.936V195.252C73.2929 195.869 73.7521 196.177 74.6707 196.177ZM82.9791 196.887C82.7049 196.962 82.4392 197.017 82.1822 197.051C81.9251 197.086 81.6492 197.103 81.3544 197.103C80.9294 197.103 80.5867 197.027 80.3262 196.877C80.0657 196.726 79.8737 196.493 79.7503 196.177C79.6269 195.862 79.5653 195.451 79.5653 194.943V190.193H77.9612V189.288H80.6552V194.892C80.6552 195.201 80.6826 195.442 80.7375 195.617C80.7957 195.788 80.9037 195.917 81.0614 196.003C81.2225 196.085 81.4573 196.126 81.7657 196.126C81.9165 196.126 82.0776 196.111 82.249 196.08C82.4238 196.049 82.626 195.999 82.8557 195.931L82.9791 196.887ZM86.7631 197.103C86.2627 197.103 85.8325 196.993 85.4726 196.774C85.1162 196.551 84.8437 196.229 84.6552 195.807C84.4701 195.382 84.3775 194.871 84.3775 194.275C84.3775 193.679 84.4701 193.17 84.6552 192.748C84.8437 192.323 85.1162 192.001 85.4726 191.782C85.8325 191.559 86.2627 191.447 86.7631 191.447C87.2635 191.447 87.692 191.559 88.0484 191.782C88.4083 192.001 88.6808 192.323 88.8659 192.748C89.0544 193.17 89.1487 193.679 89.1487 194.275C89.1487 194.871 89.0544 195.382 88.8659 195.807C88.6808 196.229 88.4083 196.551 88.0484 196.774C87.692 196.993 87.2635 197.103 86.7631 197.103ZM86.7631 196.126C87.1641 196.126 87.4726 195.967 87.6885 195.648C87.9079 195.326 88.0176 194.868 88.0176 194.275C88.0176 193.682 87.9079 193.226 87.6885 192.908C87.4726 192.585 87.1641 192.424 86.7631 192.424C86.3621 192.424 86.0519 192.585 85.8325 192.908C85.6166 193.226 85.5086 193.682 85.5086 194.275C85.5086 194.868 85.6166 195.326 85.8325 195.648C86.0519 195.967 86.3621 196.126 86.7631 196.126ZM95.5547 191.55L93.9815 196.517C93.7758 197.171 93.5462 197.696 93.2926 198.09C93.0389 198.488 92.739 198.786 92.3928 198.985C92.0466 199.187 91.6268 199.314 91.1332 199.365L90.2078 199.468L90.1564 198.491L91.0098 198.388C91.4348 198.337 91.7844 198.212 92.0586 198.013C92.3363 197.814 92.5711 197.511 92.763 197.103H92.2232L90.1049 191.55H91.236L92.9635 196.28H93.0612L93.0663 196.26L94.4237 191.55H95.5547ZM99.4004 190.686C99.2633 190.686 99.1365 190.652 99.02 190.584C98.9034 190.515 98.8109 190.423 98.7424 190.306C98.6738 190.189 98.6395 190.063 98.6395 189.926C98.6395 189.788 98.6738 189.662 98.7424 189.545C98.8109 189.429 98.9034 189.336 99.02 189.267C99.1365 189.199 99.2633 189.165 99.4004 189.165C99.5375 189.165 99.6644 189.199 99.7809 189.267C99.8974 189.336 99.99 189.429 100.059 189.545C100.127 189.662 100.161 189.788 100.161 189.926C100.161 190.063 100.127 190.189 100.059 190.306C99.99 190.423 99.8974 190.515 99.7809 190.584C99.6644 190.652 99.5375 190.686 99.4004 190.686ZM100.007 196.105H101.683V197H96.8401V196.105H98.9172V192.455H97.1485V191.55H100.007V196.105ZM105.683 191.447C106.259 191.447 106.705 191.622 107.02 191.972C107.335 192.321 107.493 192.815 107.493 193.452V197H106.403V193.452C106.403 193.11 106.317 192.844 106.146 192.656C105.974 192.467 105.735 192.373 105.426 192.373C104.569 192.373 104.141 192.894 104.141 193.936V197H103.051V191.55H104.038L104.125 192.537H104.244C104.274 192.171 104.408 191.898 104.645 191.72C104.881 191.538 105.227 191.447 105.683 191.447ZM113.662 191.55V196.517C113.662 197.168 113.565 197.701 113.369 198.116C113.177 198.53 112.878 198.842 112.47 199.051C112.062 199.264 111.534 199.382 110.886 199.406L109.292 199.458L109.241 198.491L110.989 198.409C111.534 198.385 111.932 198.232 112.182 197.951C112.435 197.67 112.562 197.247 112.562 196.681V196.157H112.47C112.435 196.465 112.29 196.7 112.033 196.861C111.776 197.022 111.438 197.103 111.02 197.103C110.561 197.103 110.168 196.997 109.842 196.784C109.52 196.568 109.273 196.253 109.102 195.838C108.934 195.423 108.85 194.919 108.85 194.327C108.85 193.72 108.934 193.201 109.102 192.769C109.273 192.337 109.522 192.009 109.848 191.787C110.177 191.56 110.571 191.447 111.03 191.447C111.465 191.447 111.801 191.538 112.038 191.72C112.278 191.898 112.422 192.171 112.47 192.537H112.588L112.675 191.55H113.662ZM111.195 196.177C112.113 196.177 112.572 195.869 112.572 195.252V193.936C112.572 192.894 112.11 192.373 111.184 192.373C110.783 192.373 110.473 192.542 110.254 192.882C110.038 193.221 109.93 193.703 109.93 194.327C109.93 194.937 110.036 195.398 110.249 195.71C110.461 196.021 110.776 196.177 111.195 196.177ZM119.668 197.103C119.53 197.103 119.404 197.069 119.287 197C119.171 196.931 119.078 196.839 119.009 196.722C118.941 196.606 118.907 196.479 118.907 196.342C118.907 196.205 118.941 196.078 119.009 195.961C119.078 195.845 119.171 195.752 119.287 195.684C119.404 195.615 119.53 195.581 119.668 195.581C119.805 195.581 119.931 195.615 120.048 195.684C120.164 195.752 120.257 195.845 120.326 195.961C120.394 196.078 120.428 196.205 120.428 196.342C120.428 196.479 120.394 196.606 120.326 196.722C120.257 196.839 120.164 196.931 120.048 197C119.931 197.069 119.805 197.103 119.668 197.103ZM123.781 197.103C123.643 197.103 123.517 197.069 123.4 197C123.284 196.931 123.191 196.839 123.122 196.722C123.054 196.606 123.02 196.479 123.02 196.342C123.02 196.205 123.054 196.078 123.122 195.961C123.191 195.845 123.284 195.752 123.4 195.684C123.517 195.615 123.643 195.581 123.781 195.581C123.918 195.581 124.044 195.615 124.161 195.684C124.278 195.752 124.37 195.845 124.439 195.961C124.507 196.078 124.541 196.205 124.541 196.342C124.541 196.479 124.507 196.606 124.439 196.722C124.37 196.839 124.278 196.931 124.161 197C124.044 197.069 123.918 197.103 123.781 197.103ZM127.894 197.103C127.757 197.103 127.63 197.069 127.513 197C127.397 196.931 127.304 196.839 127.236 196.722C127.167 196.606 127.133 196.479 127.133 196.342C127.133 196.205 127.167 196.078 127.236 195.961C127.304 195.845 127.397 195.752 127.513 195.684C127.63 195.615 127.757 195.581 127.894 195.581C128.031 195.581 128.158 195.615 128.274 195.684C128.391 195.752 128.483 195.845 128.552 195.961C128.62 196.078 128.655 196.205 128.655 196.342C128.655 196.479 128.62 196.606 128.552 196.722C128.483 196.839 128.391 196.931 128.274 197C128.158 197.069 128.031 197.103 127.894 197.103Z"
        fill="#191919"
        fill-opacity="0.5"
      />
      <path
        d="M169.758 194.46V195.365H169.069V197H168.031V195.365H164.37L164.386 195.35L165.409 190.111H166.458L165.599 194.46H168.031V189.699H169.069V194.46H169.758ZM174.786 189.185H175.825L171.64 198.44H170.601L174.786 189.185ZM180.123 196.044H181.748V197H177.213V196.044H179.033V190.707L177.316 191.098V190.111L179.033 189.699H180.123V196.044ZM185.594 197.103C183.935 197.103 183.105 195.869 183.105 193.401C183.105 190.865 183.935 189.597 185.594 189.597C187.252 189.597 188.082 190.865 188.082 193.401C188.082 195.869 187.252 197.103 185.594 197.103ZM185.594 196.157C186.519 196.157 186.982 195.238 186.982 193.401C186.982 191.495 186.519 190.543 185.594 190.543C184.668 190.543 184.205 191.495 184.205 193.401C184.205 195.238 184.668 196.157 185.594 196.157ZM185.635 194.172C185.498 194.172 185.371 194.138 185.254 194.069C185.138 194.001 185.045 193.908 184.977 193.792C184.908 193.672 184.874 193.542 184.874 193.401C184.874 193.264 184.908 193.137 184.977 193.021C185.045 192.904 185.138 192.812 185.254 192.743C185.371 192.674 185.498 192.64 185.635 192.64C185.775 192.64 185.904 192.674 186.02 192.743C186.14 192.812 186.234 192.904 186.303 193.021C186.372 193.137 186.406 193.264 186.406 193.401C186.406 193.542 186.372 193.672 186.303 193.792C186.234 193.908 186.14 194.001 186.02 194.069C185.904 194.138 185.775 194.172 185.635 194.172Z"
        fill="#191919"
        fill-opacity="0.5"
      />
      <circle
        cx="44.5"
        cy="193.5"
        r="5.95455"
        fill="#FFDB6D"
        stroke="white"
        stroke-width="3.09091"
      />
      <path
        d="M44.5 185C42.4845 185 40.5346 185.716 38.9983 187.021C37.462 188.325 36.4392 190.133 36.1124 192.122C35.7857 194.111 36.1763 196.151 37.2145 197.879C38.2527 199.606 39.871 200.908 41.7805 201.553L42.5339 199.322C41.1534 198.856 39.9835 197.914 39.2329 196.665C38.4824 195.417 38.2 193.942 38.4362 192.504C38.6724 191.066 39.4119 189.759 40.5226 188.816C41.6332 187.873 43.0429 187.355 44.5 187.355V185Z"
        fill="#FFDB6D"
      />
    </motion.g>
    <defs>
      <filter
        id="filter0_d_680_1635"
        x="-17.2241"
        y="0.414062"
        width="260.943"
        height="203.91"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feMorphology
          radius="2"
          operator="dilate"
          in="SourceAlpha"
          result="effect1_dropShadow_680_1635"
        />
        <feOffset dx="2" dy="6" />
        <feGaussianBlur stdDeviation="30" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_680_1635"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_680_1635"
          result="shape"
        />
      </filter>
      <filter
        id="filter1_d_680_1635"
        x="-37"
        y="116"
        width="303"
        height="166"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feMorphology
          radius="2"
          operator="dilate"
          in="SourceAlpha"
          result="effect1_dropShadow_680_1635"
        />
        <feOffset dx="2" dy="6" />
        <feGaussianBlur stdDeviation="30" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_680_1635"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_680_1635"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

const DesignAnimation = () => (
  <motion.svg
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1 }}
    width="100%"
    height="281"
    viewBox="0 0 226 281"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <motion.g
      opacity="0.9"
      filter="url(#filter0_d_1757_158)"
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <rect
        x="-20.2773"
        y="56.7227"
        width="128.939"
        height="128.939"
        rx="10.4829"
        fill="white"
      />
      <motion.path
        d="M10.1221 90.2695V120.46C10.1221 137.829 24.2021 151.909 41.5707 151.909H83.5023M83.5023 151.909L78.6905 147.506M83.5023 151.909L78.6905 156.312"
        stroke="url(#paint0_linear_1757_158)"
        strokeWidth="2.09658"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <circle
        cx="10.1232"
        cy="90.2687"
        r="1.57243"
        fill="url(#paint1_linear_1757_158)"
        stroke="#F2994A"
        strokeOpacity="0.3"
        strokeWidth="30"
      />
    </motion.g>
    <motion.g
      opacity="0.6"
      filter="url(#filter1_d_1757_158)"
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <rect
        x="120.723"
        y="94.7227"
        width="128.939"
        height="128.939"
        rx="10.4829"
        fill="white"
      />
      <motion.path
        d="M222.259 124.601L192.069 124.601C174.7 124.601 160.62 138.681 160.62 156.049L160.62 197.981M160.62 197.981L165.023 193.169M160.62 197.981L156.217 193.169"
        stroke="url(#paint2_linear_1757_158)"
        strokeOpacity="0.6"
        strokeWidth="2.09658"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <circle
        cx="222.262"
        cy="124.602"
        r="1.57243"
        transform="rotate(90 222.262 124.602)"
        fill="url(#paint3_linear_1757_158)"
        fillOpacity="0.6"
      />
      <circle
        cx="222.262"
        cy="124.602"
        r="5.24144"
        transform="rotate(90 222.262 124.602)"
        stroke="#F2994A"
        strokeOpacity="0.3"
        strokeWidth="7.33802"
      />
    </motion.g>
    <defs>
      <filter
        id="filter0_d_1757_158"
        x="-80.2773"
        y="0.722656"
        width="252.939"
        height="252.939"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feMorphology
          radius="2"
          operator="dilate"
          in="SourceAlpha"
          result="effect1_dropShadow_1757_158"
        />
        <feOffset dx="2" dy="6" />
        <feGaussianBlur stdDeviation="30" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_1757_158"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_1757_158"
          result="shape"
        />
      </filter>
      <filter
        id="filter1_d_1757_158"
        x="60.7227"
        y="38.7227"
        width="252.939"
        height="252.939"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feMorphology
          radius="2"
          operator="dilate"
          in="SourceAlpha"
          result="effect1_dropShadow_1757_158"
        />
        <feOffset dx="2" dy="6" />
        <feGaussianBlur stdDeviation="30" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_1757_158"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_1757_158"
          result="shape"
        />
      </filter>
      <linearGradient
        id="paint0_linear_1757_158"
        x1="46.8122"
        y1="90.2695"
        x2="46.8122"
        y2="171.414"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F19E18" />
        <stop offset="1" stopColor="#E62314" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_1757_158"
        x1="10.1232"
        y1="88.6963"
        x2="10.1232"
        y2="92.5603"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F19E18" />
        <stop offset="1" stopColor="#E62314" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_1757_158"
        x1="222.259"
        y1="161.291"
        x2="141.114"
        y2="161.291"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F19E18" />
        <stop offset="1" stopColor="#E62314" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_1757_158"
        x1="222.262"
        y1="123.029"
        x2="222.262"
        y2="126.893"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F19E18" />
        <stop offset="1" stopColor="#E62314" />
      </linearGradient>
    </defs>
  </motion.svg>
);

const MarketingAnimation = () => {
  return (
    <svg
      width="226"
      height="287"
      viewBox="0 0 226 287"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.6" filter="url(#filter0_d_680_1594)">
        <g clip-path="url(#clip0_680_1594)">
          <rect
            x="39"
            y="46"
            width="70"
            height="54"
            rx="8.83004"
            fill="white"
          />
          <path
            d="M58.26 76.2V72.9H61.54V71.7H58.26V68.4H56.98V71.7H53.72V72.9H56.98V76.2H58.26ZM66.4863 79V65.6H65.3063C64.8263 66.8 64.1463 67.26 62.8863 67.26H62.4263V68.34H65.0463V79H66.4863ZM78.3828 79V77.7H70.5228C70.5028 76.14 71.5828 75.3 73.1228 74.44L75.1428 73.32C76.8628 72.36 78.2828 71.24 78.2828 69.14C78.2828 67.1 76.9228 65.4 73.8228 65.4C70.3228 65.4 68.8228 67.42 68.8228 69.84V70.16H70.2628V69.92C70.2628 67.78 70.9628 66.72 73.7428 66.72C76.2428 66.72 76.8428 67.78 76.8428 69.24C76.8428 70.6 76.1228 71.3 74.5228 72.18L72.4428 73.34C70.5828 74.38 69.0228 75.6 69.0228 78.1V79H78.3828ZM83.143 72.04C85.183 72.04 86.243 70.48 86.243 68.72C86.243 66.96 85.183 65.4 83.143 65.4C81.123 65.4 80.063 66.96 80.063 68.72C80.063 70.48 81.123 72.04 83.143 72.04ZM82.483 79L93.263 65.6H91.823L81.043 79H82.483ZM83.143 70.96C81.783 70.96 81.243 70.18 81.243 68.72C81.243 67.22 81.783 66.48 83.143 66.48C84.523 66.48 85.063 67.22 85.063 68.72C85.063 70.18 84.523 70.96 83.143 70.96ZM91.123 79.2C93.163 79.2 94.203 77.66 94.203 75.88C94.203 74.12 93.163 72.56 91.123 72.56C89.103 72.56 88.043 74.12 88.043 75.88C88.043 77.66 89.103 79.2 91.123 79.2ZM91.123 78.12C89.743 78.12 89.223 77.36 89.223 75.88C89.223 74.4 89.743 73.64 91.123 73.64C92.503 73.64 93.043 74.4 93.043 75.88C93.043 77.36 92.503 78.12 91.123 78.12Z"
            fill="#219653"
          />
        </g>
      </g>
      <g filter="url(#filter1_d_680_1594)">
        <g clip-path="url(#clip1_680_1594)">
          <rect
            x="39"
            y="109"
            width="147.408"
            height="132.246"
            rx="8.83004"
            fill="white"
          />
          <line
            x1="47.6339"
            y1="109"
            x2="47.6339"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="59.4264"
            y1="109"
            x2="59.4264"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="71.2189"
            y1="109"
            x2="71.2189"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="83.0114"
            y1="109"
            x2="83.0114"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="94.8038"
            y1="109"
            x2="94.8038"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="106.596"
            y1="109"
            x2="106.596"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="118.389"
            y1="109"
            x2="118.389"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="130.182"
            y1="109"
            x2="130.182"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="141.974"
            y1="109"
            x2="141.974"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="153.767"
            y1="109"
            x2="153.767"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="165.559"
            y1="109"
            x2="165.559"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <line
            x1="177.352"
            y1="109"
            x2="177.352"
            y2="241.246"
            stroke="#F2F2F2"
            stroke-width="0.421165"
            stroke-dasharray="0.84 0.84"
          />
          <motion.path
            d="M47.3809 191.127L39 196.602V241.667H186.408V164.624L162.949 170.911L144.864 161.225L131.631 173.438L123.691 170.911L110.458 181.862L103.842 178.071L93.6966 191.127L80.0224 162.909L66.7894 191.127H47.3809Z"
            fill="url(#paint0_linear_680_1594)"
            fillOpacity="0.5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{
              duration: 4,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 1],
            }}
          />
          <motion.path
            d="M38.1572 196.602L47.3805 191.127H66.7889L80.022 162.909L93.6961 191.127L103.841 178.071L110.458 181.862L123.691 170.911L131.631 173.438L144.864 161.225L162.949 170.911L188.092 164.173"
            stroke="#F2994A"
            strokeWidth="0.5"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 4,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 1]

            }}
          />

          <motion.g
            initial={{ x: 38.1572, y: 196.602 }}
            animate={{
              x: [
                38.1572, 47.3805, 66.7889, 80.022, 93.6961, 103.841, 110.458,
                123.691, 131.631, 144.864, 162.949, 188.092,
              ],
              y: [
                196.602, 191.127, 191.127, 162.909, 191.127, 178.071, 181.862,
                170.911, 173.438, 161.225, 170.911, 164.173,
              ],
            }}
            transition={{
              duration: 4,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 1],
            }}
          >
            <circle r="5.47514" fill="#F28C28" fillOpacity="0.2" />
            <circle r="2.44815" fill="white" stroke="#F28C28" />
          </motion.g>
        </g>
      </g>
      <g filter="url(#filter3_d_680_1594)">
        <g clip-path="url(#clip2_680_1594)">
          <rect
            x="116"
            y="46"
            width="70"
            height="54"
            rx="8.83004"
            fill="white"
          />
          <path
            opacity="0.5"
            d="M135.98 73.78V72.52H130.52V73.78H135.98ZM147.199 79V77.7H139.339C139.319 76.14 140.399 75.3 141.939 74.44L143.959 73.32C145.679 72.36 147.099 71.24 147.099 69.14C147.099 67.1 145.739 65.4 142.639 65.4C139.139 65.4 137.639 67.42 137.639 69.84V70.16H139.079V69.92C139.079 67.78 139.779 66.72 142.559 66.72C145.059 66.72 145.659 67.78 145.659 69.24C145.659 70.6 144.939 71.3 143.339 72.18L141.259 73.34C139.399 74.38 137.839 75.6 137.839 78.1V79H147.199ZM153.907 79.2C156.847 79.2 158.747 77.88 158.747 75.4C158.747 73.54 157.707 72.3 155.847 71.98V71.9C157.527 71.6 158.347 70.42 158.347 68.9C158.347 66.64 156.647 65.4 153.787 65.4C150.427 65.4 148.827 67.28 148.827 69.7V69.94H150.247V69.76C150.247 67.7 151.087 66.72 153.767 66.72C156.087 66.72 156.907 67.44 156.907 69.08C156.907 70.72 155.987 71.4 153.807 71.4H151.687V72.62H154.107C156.327 72.62 157.307 73.38 157.307 75.28C157.307 77.12 156.387 77.88 153.907 77.88C151.007 77.88 150.247 76.9 150.247 74.84V74.58H148.827V74.9C148.827 77.32 150.247 79.2 153.907 79.2ZM163.151 72.04C165.191 72.04 166.251 70.48 166.251 68.72C166.251 66.96 165.191 65.4 163.151 65.4C161.131 65.4 160.071 66.96 160.071 68.72C160.071 70.48 161.131 72.04 163.151 72.04ZM162.491 79L173.271 65.6H171.831L161.051 79H162.491ZM163.151 70.96C161.791 70.96 161.251 70.18 161.251 68.72C161.251 67.22 161.791 66.48 163.151 66.48C164.531 66.48 165.071 67.22 165.071 68.72C165.071 70.18 164.531 70.96 163.151 70.96ZM171.131 79.2C173.171 79.2 174.211 77.66 174.211 75.88C174.211 74.12 173.171 72.56 171.131 72.56C169.111 72.56 168.051 74.12 168.051 75.88C168.051 77.66 169.111 79.2 171.131 79.2ZM171.131 78.12C169.751 78.12 169.231 77.36 169.231 75.88C169.231 74.4 169.751 73.64 171.131 73.64C172.511 73.64 173.051 74.4 173.051 75.88C173.051 77.36 172.511 78.12 171.131 78.12Z"
            fill="#EB5757"
          />
        </g>
      </g>
      <defs>
        <filter
          id="filter0_d_680_1594"
          x="-29.9487"
          y="-13.4386"
          width="216.908"
          height="201.408"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="2.37754"
            operator="dilate"
            in="SourceAlpha"
            result="effect1_dropShadow_680_1594"
          />
          <feOffset dx="4.75509" dy="14.2653" />
          <feGaussianBlur stdDeviation="35.6631" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0.05 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_680_1594"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_680_1594"
            result="shape"
          />
        </filter>
        <filter
          id="filter1_d_680_1594"
          x="-29.9487"
          y="49.5614"
          width="294.815"
          height="279.654"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="2.37754"
            operator="dilate"
            in="SourceAlpha"
            result="effect1_dropShadow_680_1594"
          />
          <feOffset dx="4.75509" dy="14.2653" />
          <feGaussianBlur stdDeviation="35.6631" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0.05 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_680_1594"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_680_1594"
            result="shape"
          />
        </filter>
        <filter
          id="filter2_d_680_1594"
          x="70.1664"
          y="124.163"
          width="115.399"
          height="115.399"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="1.68466"
            operator="dilate"
            in="SourceAlpha"
            result="effect1_dropShadow_680_1594"
          />
          <feOffset dx="3.36932" dy="10.108" />
          <feGaussianBlur stdDeviation="25.2699" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0.05 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_680_1594"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_680_1594"
            result="shape"
          />
        </filter>
        <filter
          id="filter3_d_680_1594"
          x="47.0513"
          y="-13.4386"
          width="216.908"
          height="201.408"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="2.37754"
            operator="dilate"
            in="SourceAlpha"
            result="effect1_dropShadow_680_1594"
          />
          <feOffset dx="4.75509" dy="14.2653" />
          <feGaussianBlur stdDeviation="35.6631" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0 0.733333 0 0 0 0.05 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_680_1594"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_680_1594"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear_680_1594"
          x1="113.546"
          y1="109.421"
          x2="113.546"
          y2="256.829"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#F19E18" />
          <stop offset="1" stop-color="#F19E18" stop-opacity="0" />
        </linearGradient>
        <clipPath id="clip0_680_1594">
          <rect
            x="39"
            y="46"
            width="69.5"
            height="54"
            rx="8.82761"
            fill="white"
          />
        </clipPath>
        <clipPath id="clip1_680_1594">
          <rect
            x="39"
            y="109"
            width="147.408"
            height="132.246"
            rx="8.82761"
            fill="white"
          />
        </clipPath>
        <clipPath id="clip2_680_1594">
          <rect
            x="116"
            y="46"
            width="69.5"
            height="54"
            rx="8.82761"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const FinanceAnimation = () => {
  const lineVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { 
      pathLength: 1, 
      opacity: 1,
      transition: { 
        duration: 5,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "loop"
      }
    }
  };

  const areaVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 0.5,
      transition: { 
        duration: 5,
        ease: "easeInOut",
        delay: 1,
        repeat: Infinity,
        repeatType: "loop"
      }
    }
  };

  return (
    <svg width="100%" height="156" viewBox="0 0 174 156" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_1782_179)">
        <rect width="188" height="168" rx="8.83004" fill="white"/>
        {/* Background grid lines */}
        {[...Array(11)].map((_, i) => (
          <line 
            key={i}
            x1={11.1622 + i * 15.0098} 
            y1="0" 
            x2={11.1622 + i * 15.0098} 
            y2="168.327" 
            stroke="#C2C2C2" 
            strokeWidth="0.421165" 
            strokeDasharray="1.5 6"
          />
        ))}
        
        {/* Animated graph lines */}
        <motion.path
          d="M0 100 Q30 80 60 90 T120 70 T180 50"
          stroke="#F19E18"
          strokeWidth="2"
          fill="none"
          variants={lineVariants}
          initial="hidden"
          animate="visible"
        />
        <motion.path
          d="M0 120 Q40 100 80 110 T174 90"
          stroke="#E62314"
          strokeWidth="2"
          fill="none"
          variants={lineVariants}
          initial="hidden"
          animate="visible"
        />
        
        {/* Animated areas under the graphs */}
        <motion.path
          d="M0 100 Q30 80 60 90 T120 70 T180 50 V168 H0 Z"
          fill="url(#paint0_linear)"
          variants={areaVariants}
          initial="hidden"
          animate="visible"
        />
        <motion.path
          d="M0 120 Q40 100 80 110 T174 90 V168 H0 Z"
          fill="url(#paint1_linear)"
          variants={areaVariants}
          initial="hidden"
          animate="visible"
        />
        
      </g>
      <defs>
        <linearGradient id="paint0_linear" x1="90" y1="50" x2="90" y2="168" gradientUnits="userSpaceOnUse">
          <stop stopColor="#F19E18" stopOpacity="0.5"/>
          <stop offset="1" stopColor="#F19E18" stopOpacity="0"/>
        </linearGradient>
        <linearGradient id="paint1_linear" x1="80" y1="90" x2="80" y2="168" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E62314" stopOpacity="0.5"/>
          <stop offset="1" stopColor="#E62314" stopOpacity="0"/>
        </linearGradient>
        <clipPath id="clip0_1782_179">
          <rect width="174" height="156" rx="8.82761" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
};

const VentureCapitalAnimation = () => {
  const duration = 10; // Slowed down animation duration
  const orange = "#F19E18";
  const lightOrange = "#F8D3A7";
  const gray = "#D1D1D1";

  return (
    <svg width="166" height="166" viewBox="0 0 166 166" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="166" height="166" rx="13.5" fill="white"/>
      
      {/* Background grid */}
      <path d="M0 41.5H166 M0 83H166 M0 124.5H166" stroke={gray} strokeWidth="0.5" />
      <path d="M41.5 0V166 M83 0V166 M124.5 0V166" stroke={gray} strokeWidth="0.5" />

      {/* Central circle representing the VC fund */}
      <motion.circle
        cx="83"
        cy="83"
        r="30"
        stroke={orange}
        strokeWidth="2"
        fill="none"
        initial={{ scale: 0.9, opacity: 0.5 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          duration: duration / 2,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut"
        }}
      />

      {/* Investment branches */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i * Math.PI) / 2;
        const endX = 83 + Math.cos(angle) * 60;
        const endY = 83 + Math.sin(angle) * 60;
        return (
          <motion.path
            key={`branch-${i}`}
            d={`M83,83 Q${83 + Math.cos(angle) * 30},${83 + Math.sin(angle) * 30} ${endX},${endY}`}
            stroke={orange}
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: duration,
              delay: i * duration / 4,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />
        );
      })}

      {/* Investment growth indicators */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i * Math.PI) / 2;
        const centerX = 83 + Math.cos(angle) * 60;
        const centerY = 83 + Math.sin(angle) * 60;
        return (
          <motion.circle
            key={`growth-${i}`}
            cx={centerX}
            cy={centerY}
            r="10"
            fill={lightOrange}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              duration: duration / 2,
              delay: i * duration / 4 + duration / 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />
        );
      })}

      {/* Flowing capital representation */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i * Math.PI) / 2;
        const endX = 83 + Math.cos(angle) * 60;
        const endY = 83 + Math.sin(angle) * 60;
        return (
          <motion.circle
            key={`capital-${i}`}
            r="4"
            fill={orange}
            initial={{ cx: 83, cy: 83, opacity: 0 }}
            animate={{ 
              cx: [83, endX, 83],
              cy: [83, endY, 83],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: duration,
              delay: i * duration / 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        );
      })}

      {/* Central pulsating dot */}
      <motion.circle
        cx="83"
        cy="83"
        r="5"
        fill={orange}
        initial={{ scale: 1 }}
        animate={{ scale: [1, 1.5, 1] }}
        transition={{
          duration: duration / 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </svg>
  );
};

const DivisionCard = ({ title, data }) => {
  const AnimationComponent = {
    "Product": ProductManagementAnimation,
    Engineering: EngineeringAnimation,
    Design: DesignAnimation,
    Marketing: MarketingAnimation,
    Finance: FinanceAnimation,
    "Venture Capital": VentureCapitalAnimation,
  }[title];

  return (
    <motion.div
      className={`bg-white rounded-lg shadow-lg overflow-hidden ring ring-[#F2F2F2]`}
      whileHover={{ scale: 1.03 }}
      layout
    >
      <div className="flex h-full">
        <div className="w-[40%] min-w-[220px] h-full bg-gradient-to-tr from-[#FDF0D5] via-[#FADCBD] to-[#F9D1D1] flex items-center justify-center">
          <AnimationComponent color={data.color} />
        </div>
        <div className="w-[65%] px-8 py-8">
          <div className="flex items-center mb-3">
            <h3 className="text-xl font-semibold">{title}</h3>
          </div>
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-gray-500 text-sm font-light mb-4 max-w-[90%]">
                {data.description}
              </p>
              <ul className="list-disc list-inside text-gray-600 mt-1">
                {data.tasks.map((task, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-sm font-light mb-2 ml-3"
                  >
                    {task}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

const WhatWeDo = () => {
  return (
    <div className="w-full min-h-screen py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row justify-between items-start mb-16">
          <div className="lg:w-1/2 mb-8 lg:mb-0">
            <h3 className="text-xl text-gray-400 font-medium mb-4 uppercase tracking-wider">
              WHAT WE OFFER
            </h3>
            <h1 className="text-4xl sm:text-5xl max-w-xl leading-tight sm:leading-snug text-gray-800 font-bold mb-6 text-left">
              Comprehensive support for 8 weeks
            </h1>
            <h4 className="text-lg text-gray-500 font-thin max-w-lg">
              Dedicated, multidisciplinary teams collaborating with startup
              founders to achieve their goals through specialized expertise and
              hands-on support.
            </h4>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(TeamData).map(([title, data]) => (
            <DivisionCard key={title} title={title} data={data} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WhatWeDo;
